import moment from "moment";
import { isEmpty, cloneDeep } from "lodash";

import { calcOperatingCosts } from "utils/upgrades";
import { getProvName } from "utils/weather";
import { getNetTotalEnergyConsumption } from "utils/results";
import { getLabel } from "utils/dcfExport/details";
import convertUnit from "utils/convertUnit";
import {
    SORTED_UPGRADES_TYPES,
    WATERFALL_DIAGNOSTIC_FOCUS_BASE_MODEL_IDS,
    WATERFALL_DIAGNOSTIC_PROBE,
} from "./constants";
import { energyConversion } from "utils/constants/energyConversion";
import { extractCodesFromCodeLib } from "utils/codes";
import {
    collectEcmSubTypesForPackageUpgrade,
    resolveCompositeEcmRowLabel,
    buildCompositePackageLabelMap,
    COMBINED_H2K_ECM_SUB_TYPES,
} from "utils/packageUpgradeEcmCategories";
import { parseSemicolonUpgradePairs } from "utils/runResultUpgradeDisplay";

/*****************************************************
    COMMUNITY REGION
*****************************************************/
const regionGroups = {
    // CAN: ["BC", "AB", "SK", "MB", "ON", "QC", "NB", "PE", "NS", "NL", "YT", "NT", "NU"],
    ATL: ["NB", "PE", "NS", "NL"],
    PR: ["AB", "SK", "MB"],
    NORTH: ["YT", "NT", "NU"],
};

//Upgrade category groupings
const ENVELOPE_CATEGORIES = [
    "ceilingWithAttic",
    "cathedral",
    "wall",
    "expFloor",
    "floorHeader",
    "basementWall",
    "basementSlab",
    "crawlspace",
    "slab",
    "window",
    "skylights",
    "door",
    "airTightness",
];

const HVAC_CATEGORIES = ["ventilation", "heatingCooling", "spaceHeating", "secondaryHeating", "spaceCooling"];

const DHW_CATEGORIES = ["primaryHotWater", "dwhr"];

const OTHER_CATEGORIES = ["generation", "transportation", "baseLoads"];

const CHECKPOINT_SAVINGS_TEMPLATE = {
    ENV: {
        divideTotalBy: 0,
        upgradePackageId: "",
        percentEmissionSavings: 0,
        percentEnergySavings: 0,
        percentEnergySavingsArray: [],
        percentEmissionSavingsArray: [],
        /** @type {Array<Object>} One object per contributing run; see {@link fillCheckpointUpgradeSavings} cascade for inherited rows */
        auditContributions: [],
    },
    ENV_HVAC: {
        divideTotalBy: 0,
        upgradePackageId: "",
        percentEmissionSavings: 0,
        percentEnergySavings: 0,
        percentEnergySavingsArray: [],
        percentEmissionSavingsArray: [],
        auditContributions: [],
    },
    ENV_HVAC_DHW: {
        divideTotalBy: 0,
        upgradePackageId: "",
        percentEmissionSavings: 0,
        percentEnergySavings: 0,
        percentEnergySavingsArray: [],
        percentEmissionSavingsArray: [],
        auditContributions: [],
    },
    TOTAL: {
        divideTotalBy: 0,
        upgradePackageId: "",
        percentEmissionSavings: 0,
        percentEnergySavings: 0,
        percentEnergySavingsArray: [],
        percentEmissionSavingsArray: [],
        auditContributions: [],
    },
};

/** Broadest → narrowest. When a checkpoint’s % savings arrays are empty, copy from the nearest broader checkpoint with data. */
const CHECKPOINT_SAVINGS_CASCADE = ["TOTAL", "ENV_HVAC_DHW", "ENV_HVAC", "ENV"];

const CHECKPOINT_AUDIT_LABELS = {
    ENV: "Envelope (ENV)",
    ENV_HVAC: "Envelope + HVAC",
    ENV_HVAC_DHW: "Envelope + HVAC + DHW",
    TOTAL: "Whole package (TOTAL)",
};

const WF_AUDIT_TEXT_MAX = 480;

const truncateAuditText = (v, max = WF_AUDIT_TEXT_MAX) => {
    if (v == null) {
        return v;
    }
    const s = typeof v === "string" ? v : JSON.stringify(v);
    if (s.length <= max) {
        return s;
    }
    return `${s.slice(0, max)}…`;
};

/**
 * Builds the object stored on `runGaps.checkpointAuditTrail` for UI / console inspection.
 * @param {Object} filledCheckpointUpgradeSavings - output of {@link fillCheckpointUpgradeSavings} (after any TOTAL GJ overrides)
 */
const buildCheckpointAuditTrailForRunGaps = (filledCheckpointUpgradeSavings) => {
    if (!filledCheckpointUpgradeSavings) {
        return null;
    }
    const tiers = {};
    for (const key of ["TOTAL"]) {
        const e = filledCheckpointUpgradeSavings[key];
        const contribs = Array.isArray(e?.auditContributions) ? e.auditContributions : [];
        let wSum = 0;
        let eNum = 0;
        let mNum = 0;
        contribs.forEach((c) => {
            const w = Number(c.duplicateUnitsInMean);
            if (!Number.isFinite(w) || w <= 0) {
                return;
            }
            wSum += w;
            eNum += w * (Number(c.percentEnergySavings) || 0);
            mNum += w * (Number(c.percentEmissionSavings) || 0);
        });
        tiers[key] = {
            key,
            label: CHECKPOINT_AUDIT_LABELS[key] || key,
            chartPercentEnergySavings: e?.percentEnergySavings ?? null,
            chartPercentEmissionSavings: e?.percentEmissionSavings ?? null,
            duplicateWeightedMeanEnergy: wSum > 0 ? eNum / wSum : null,
            duplicateWeightedMeanEmissions: wSum > 0 ? mNum / wSum : null,
            totalDuplicateUnitsInMean: wSum,
            distinctRunContributions: contribs.length,
            contributions: contribs,
        };
    }
    return {
        explanation:
            "TOTAL only: duplicate-weighted mean of whole-package run % (runResults), then overridden by GJ-weighted community % when available. Intermediate ENV / ENV_HVAC / ENV_HVAC_DHW combined by-measure rows are no longer used for waterfall scaling. Per-measure bars use GJ deltas scaled to the package anchor (see getWaterfallData). duplicateUnitsInMean repeats the row when Duplicate multiplier is on. Absolute GJ/tCO₂ columns: baseline = base-case row for that orientation; scenario = this run’s row; duplicate multiplier divided out.",
        tiers,
    };
};

/** Whole-package reference only (GJ-weighted); intermediate ENV / ENV_HVAC tiers are not used for the chart. */
const summarizeCheckpointsForWaterfallChart = (filledCheckpointUpgradeSavings) => {
    if (!filledCheckpointUpgradeSavings) {
        return null;
    }
    const t = filledCheckpointUpgradeSavings.TOTAL;
    return {
        TOTAL: {
            percentEnergySavings: t?.percentEnergySavings ?? 0,
            percentEmissionSavings: t?.percentEmissionSavings ?? 0,
        },
    };
};

const mean = (arr) => (Array.isArray(arr) && arr.length > 0 ? arr.reduce((sum, n) => sum + n, 0) / arr.length : 0);

/**
 * Fills a checkpoint upgrade savings object after initial population: backfills empty percent*Arrays from broader
 * checkpoints, then sets percentEnergySavings and percentEmissionSavings to the array averages.
 * @param {Object} checkpointUpgradeSavings - clone of {@link CHECKPOINT_SAVINGS_TEMPLATE} with arrays filled from runs
 * @returns {Object} new filled object (input is not mutated)
 */
export const fillCheckpointUpgradeSavings = (checkpointUpgradeSavings = {}) => {
    const out = cloneDeep(checkpointUpgradeSavings);
    const chain = CHECKPOINT_SAVINGS_CASCADE;

    const backfillArray = (key, arrayProp) => {
        const idx = chain.indexOf(key);
        if (idx <= 0) {
            return;
        }
        const entry = out[key];
        if (!entry || !isEmpty(entry[arrayProp])) {
            return;
        }
        for (let j = idx - 1; j >= 0; j--) {
            const source = out[chain[j]]?.[arrayProp];
            if (!isEmpty(source)) {
                entry[arrayProp] = [...source];
                break;
            }
        }
    };

    for (const key of chain) {
        backfillArray(key, "percentEnergySavingsArray");
        backfillArray(key, "percentEmissionSavingsArray");
    }

    const backfillAudit = (key) => {
        const idx = chain.indexOf(key);
        if (idx <= 0) {
            return;
        }
        const entry = out[key];
        if (!entry || !isEmpty(entry.auditContributions)) {
            return;
        }
        for (let j = idx - 1; j >= 0; j -= 1) {
            const source = out[chain[j]]?.auditContributions;
            if (!isEmpty(source)) {
                entry.auditContributions = source.map((c) => ({
                    ...c,
                    inheritedFromCheckpoint: chain[j],
                }));
                break;
            }
        }
    };

    for (const key of chain) {
        backfillAudit(key);
    }

    for (const key of chain) {
        const entry = out[key];
        if (!entry) {
            continue;
        }
        entry.percentEnergySavings = mean(entry.percentEnergySavingsArray);
        entry.percentEmissionSavings = mean(entry.percentEmissionSavingsArray);
    }

    return out;
};

const rawAverage = (u, field) => {
    if (!u) {
        return 0;
    }
    const { divideTotalBy = 1 } = u;
    if (field === "energy") {
        return u.upgradePercentEnergySavings / divideTotalBy || 0;
    }
    return u.upgradePercentEmissionSavings / divideTotalBy || 0;
};

/** Same non-zero % test used for package run rows and by-measure rows (keeps checkpoint totals aligned with merges). */
const rowHasPercentSavings = (pctEn, pctEm) =>
    (pctEn != null && pctEn !== 0) || (pctEm != null && pctEm !== 0);

/**
 * Force the sum of per-measure % (types present in `allUpgrades`) to equal `targetPercent` (e.g. whole-package anchor).
 */
const rescaleMeasurePercentMapToTotal = (measureMap, allUpgrades, targetPercent) => {
    if (targetPercent == null || !Number.isFinite(targetPercent)) {
        return measureMap;
    }
    const types = SORTED_UPGRADES_TYPES.filter((t) => allUpgrades[t]);
    if (types.length === 0) {
        return measureMap;
    }
    let sum = types.reduce((s, t) => s + (measureMap[t] ?? 0), 0);
    if (Math.abs(sum) < 1e-9) {
        const even = targetPercent / types.length;
        const out = { ...measureMap };
        types.forEach((t) => {
            out[t] = even;
        });
        return out;
    }
    const k = targetPercent / sum;
    const out = { ...measureMap };
    types.forEach((t) => {
        out[t] = (measureMap[t] ?? 0) * k;
    });
    return out;
};

/**
 * Site energy for weighting: **netGJ** (consumption after on-site generation) when present — matches API `percentSavings`
 * and CSV “Net Energy Consumption” for PV packages. Falls back to **aecGJ** when `netGJ` is missing (legacy rows).
 */
const siteEnergyGJForPackageWeighting = (result) => Number(result?.netGJ ?? result?.aecGJ ?? 0) || 0;

/** Undo duplicate-count scaling on maps keyed by `uniqueModelId` when `applyModelResultMultiplier` is on. */
const perOrientationDuplicateDivisor = (applyModelResultMultiplier, modelResultMultiplier) => {
    if (!applyModelResultMultiplier) {
        return 1;
    }
    const w = Number(modelResultMultiplier);
    if (!Number.isFinite(w) || w <= 0) {
        return 1;
    }
    return w;
};

/**
 * Baseline vs scenario absolutes for one result row (single orientation, unweighted duplicate count).
 * GJ baseline/scenario both use **netGJ ?? aecGJ** for scenario (see {@link siteEnergyGJForPackageWeighting}); raw aec/net included for cross-checks.
 */
const auditAbsoluteSnapshotForOrientation = (
    uniqueModelId,
    result,
    baseSiteGJWeighted,
    baseEmissions,
    applyModelResultMultiplier,
    modelResultMultiplier
) => {
    const div = perOrientationDuplicateDivisor(applyModelResultMultiplier, modelResultMultiplier);
    const baselineSiteGJ = (baseSiteGJWeighted[uniqueModelId] || 0) / div;
    const baselineEmissionsTCO2 = (baseEmissions[uniqueModelId] || 0) / div;
    const scenarioSiteOrNetGJ = siteEnergyGJForPackageWeighting(result);
    const scenarioAecGJ = Number(result?.aecGJ) || 0;
    const scenarioNetGJRaw = result?.netGJ;
    const scenarioNetGJ =
        scenarioNetGJRaw != null && Number.isFinite(Number(scenarioNetGJRaw)) ? Number(scenarioNetGJRaw) : null;
    const scenarioEmissionsTCO2 = Number(result?.totalEmissionsTCO2) || 0;
    return {
        baselineSiteGJ,
        baselineEmissionsTCO2,
        scenarioSiteOrNetGJ,
        scenarioAecGJ,
        scenarioNetGJ,
        scenarioEmissionsTCO2,
    };
};

/**
 * Community “whole package” % from weighted site energy / emissions (aligned with reported `percentSavings` when
 * that uses net energy for PV), vs checkpoint `mean(percentSavings)` which can differ for other reasons.
 */
const computeGjWeightedPackageSavingsPercents = (
    runResults,
    selectedModels,
    selectedUpgradePackage,
    communityModelCountsMap,
    applyModelResultMultiplier
) => {
    const baseSiteGJ = {};
    const pkgSiteGJ = {};
    const baseEm = {};
    const pkgEm = {};

    const weight = (uniqueModelId) => {
        const m = Math.max(0, communityModelCountsMap[uniqueModelId] || 1);
        return applyModelResultMultiplier ? m : 1;
    };

    runResults?.forEach(({ results }) => {
        if (!results) {
            return;
        }
        Object.entries(results).forEach(([resultsId, result]) => {
            const [uniqueModelId] = resultsId.split("-");
            const [baseModelId] = uniqueModelId.split("_");
            if (!selectedModels.includes(baseModelId)) {
                return;
            }
            if (result.result === "FAIL") {
                return;
            }

            const w = weight(uniqueModelId);
            const siteGJ = siteEnergyGJForPackageWeighting(result);
            const em = result.totalEmissionsTCO2 || 0;
            const pkgId = result.upgradePackageId;

            if (pkgId === "base") {
                if (siteGJ > 0) {
                    baseSiteGJ[uniqueModelId] = (baseSiteGJ[uniqueModelId] || 0) + siteGJ * w;
                }
                if (em > 0) {
                    baseEm[uniqueModelId] = (baseEm[uniqueModelId] || 0) + em * w;
                }
            } else if (pkgId === selectedUpgradePackage) {
                pkgSiteGJ[uniqueModelId] = (pkgSiteGJ[uniqueModelId] || 0) + siteGJ * w;
                pkgEm[uniqueModelId] = (pkgEm[uniqueModelId] || 0) + em * w;
            }
        });
    });

    let sumBaseSite = 0;
    let sumPkgSite = 0;
    let nEnergyModels = 0;
    Object.keys(pkgSiteGJ).forEach((uid) => {
        const b = baseSiteGJ[uid];
        if (b > 0) {
            nEnergyModels += 1;
            sumBaseSite += b;
            sumPkgSite += pkgSiteGJ[uid] || 0;
        }
    });
    const energyPct = sumBaseSite > 0 ? 100 * (1 - sumPkgSite / sumBaseSite) : null;

    let sumBaseEm = 0;
    let sumPkgEm = 0;
    let nEmModels = 0;
    Object.keys(pkgEm).forEach((uid) => {
        const b = baseEm[uid];
        if (b > 0) {
            nEmModels += 1;
            sumBaseEm += b;
            sumPkgEm += pkgEm[uid] || 0;
        }
    });
    const emissionsPct = sumBaseEm > 0 ? 100 * (1 - sumPkgEm / sumBaseEm) : null;

    return {
        energy: energyPct,
        emissions: emissionsPct,
        detail: {
            modelsWithBaseAndPackageEnergy: nEnergyModels,
            /** Weighted sum of `netGJ ?? aecGJ` (site / net energy, includes effect of solar). */
            sumWeightedBaseSiteGJ: sumBaseSite,
            sumWeightedPackageSiteGJ: sumPkgSite,
            modelsWithBaseAndPackageEmissions: nEmModels,
            sumWeightedBaseEmissionsTCO2: sumBaseEm,
            sumWeightedPackageEmissionsTCO2: sumPkgEm,
        },
    };
};

/**
 * Scale per-measure **signed** community deltas (GJ or tCO₂) so their sum equals the whole-package weighted delta.
 * Percents for the chart are `100 * scaledDelta / denom` (community baseline denominator).
 * Handles mixed-sign raw rows (e.g. cooling adds load while envelope reduces heating).
 */
const scaleSignedMeasureDeltasToPackageTarget = (typesPresent, rawByType, targetDelta, denom) => {
    const percentByType = {};
    const n = typesPresent.length;
    if (n === 0 || denom <= 0) {
        return percentByType;
    }
    const rawSum = typesPresent.reduce((s, t) => s + (Number(rawByType[t]) || 0), 0);
    const target = Number(targetDelta) || 0;

    if (Math.abs(rawSum) < 1e-9) {
        if (Math.abs(target) < 1e-9) {
            typesPresent.forEach((t) => {
                percentByType[t] = 0;
            });
        } else {
            const even = target / n;
            typesPresent.forEach((t) => {
                percentByType[t] = (100 * even) / denom;
            });
        }
        return percentByType;
    }

    const k = target / rawSum;
    typesPresent.forEach((t) => {
        const scaled = (Number(rawByType[t]) || 0) * k;
        percentByType[t] = (100 * scaled) / denom;
    });
    return percentByType;
};

/**
 * Waterfall bars: scale each measure’s implied savings to sum to the GJ-weighted whole-package anchor (sequence-agnostic).
 * Energy uses weighted Σ(base GJ − scenario GJ) per type vs package total; emissions analogous in tCO₂.
 * When GJ deltas are unavailable, falls back to rescaling mean-of-API % to the same package % anchor.
 */
const computePackageAnchoredScaledMeasurePercents = (
    allUpgrades,
    deltaGJByType,
    deltaEmByType,
    useDeltaForMeasures,
    gjAnchors,
    denomSiteGJ,
    denomEmissions,
    targetEnergyPercentForFallback,
    targetEmissionsPercentForFallback
) => {
    const typesPresent = SORTED_UPGRADES_TYPES.filter((t) => allUpgrades[t]);
    const d = gjAnchors?.detail || {};
    const targetEnergyGJ =
        d.sumWeightedBaseSiteGJ > 0 ? d.sumWeightedBaseSiteGJ - d.sumWeightedPackageSiteGJ : null;
    const targetEmissions =
        d.sumWeightedBaseEmissionsTCO2 > 0 ? d.sumWeightedBaseEmissionsTCO2 - d.sumWeightedPackageEmissionsTCO2 : null;

    const anchorE =
        gjAnchors.energy != null && Number.isFinite(gjAnchors.energy) ? gjAnchors.energy : targetEnergyPercentForFallback;
    const anchorM =
        gjAnchors.emissions != null && Number.isFinite(gjAnchors.emissions)
            ? gjAnchors.emissions
            : targetEmissionsPercentForFallback;

    let energy = {};
    let emissions = {};

    if (useDeltaForMeasures && targetEnergyGJ != null && denomSiteGJ > 0) {
        const rawGJ = {};
        typesPresent.forEach((t) => {
            rawGJ[t] = deltaGJByType[t] || 0;
        });
        energy = scaleSignedMeasureDeltasToPackageTarget(typesPresent, rawGJ, targetEnergyGJ, denomSiteGJ);
    } else {
        const rawMap = {};
        typesPresent.forEach((t) => {
            rawMap[t] = rawAverage(allUpgrades[t], "energy");
        });
        energy = rescaleMeasurePercentMapToTotal(rawMap, allUpgrades, anchorE);
    }

    if (useDeltaForMeasures && targetEmissions != null && denomEmissions > 0) {
        const rawEm = {};
        typesPresent.forEach((t) => {
            rawEm[t] = deltaEmByType[t] || 0;
        });
        emissions = scaleSignedMeasureDeltasToPackageTarget(typesPresent, rawEm, targetEmissions, denomEmissions);
    } else {
        const rawMapEm = {};
        typesPresent.forEach((t) => {
            rawMapEm[t] = rawAverage(allUpgrades[t], "emissions");
        });
        emissions = rescaleMeasurePercentMapToTotal(rawMapEm, allUpgrades, anchorM);
    }

    return { energy, emissions };
};

// Determine whether every element in an array (subsetArray) is present in a larger array (parentArray)
let checkSubset = (parentArray, subsetArray) => {
    return subsetArray.every((el) => {
        return parentArray.includes(el);
    });
};

// Return the general region given a list of canadian provinces.
export const getRegion = (provinceList = []) => {
    // Remove duplicate provinces from list
    var provinces = [...new Set(provinceList)];

    // Default region is "CAN"
    var region = "CAN";

    if (provinces.length >= 3) {
        // If 3-4 provinces are in the community, check for any subgroups, else display "CAN"
        if (provinces.length <= 4) {
            Object.keys(regionGroups).forEach((key) => {
                if (checkSubset(regionGroups[key], provinces)) region = key;
            });
        }
        // Return region name if subregion found or more than 3 provinces exist, otherwise continue outside if statement
        if (region !== "CAN" || provinces.length > 3) return region;
    }
    // If 3 or less provinces and no subgroup found, list each province, but if 0 provinces are present default to "CAN"
    return provinces.length > 0 ? provinces.join(", ") : region;
};

/*****************************************************
    COMMUNITY RESULTS
*****************************************************/
// Get upgrade packages data from model
const grabPackage = (id, result, upgrade, fuelPrices, isBase = false) => {
    let packageResult = isBase ? result.baseCase : result.allUpgrades;
    let { annualEnergyConsumption } = getNetTotalEnergyConsumption(packageResult);
    let { annualEmissions } = packageResult.resultsSummary;
    let operatingCost =
        fuelPrices && !isEmpty(fuelPrices) ? calcOperatingCosts(result.allUpgrades, fuelPrices, "total") : null;

    let packageData = {
        id: isBase ? "base" : id,
        name: isBase ? "Base Case" : upgrade.name,
        displayOrder: isBase ? 0 : upgrade.ui?.displayOrder,
        energyConsumption: annualEnergyConsumption,
        opEmissions: annualEmissions,
        opCost: operatingCost,
    };

    return {
        annualEnergyConsumption,
        annualEmissions,
        operatingCost,
        packageData,
    };
};

// Extract necessary information from a particular model's data to populate its graph
export const getCommModelResults = (
    models,
    key,
    { details: { fuelPrices = {}, totalRenovationCost = null }, upgrades, results }
) => {
    var allEnergies = [];
    var allEmissions = [];
    var allOpCost = [];
    var data = [];

    // Collect ids for packages with results
    const packageIds = Object.keys(results).filter((id) => !isEmpty(results[id].allUpgrades) && upgrades[id]);

    if (isEmpty(packageIds)) return null;

    //Volta Dev tests to be filtered out
    if (["NkHImmysAMGcC0Fs7g7d", "no2a5fvFXtzZ2CsrXdIr"].includes(key)) return null;

    packageIds.forEach((id, index) => {
        // Add base case on the first run
        if (index === 0) {
            let { annualEnergyConsumption, annualEmissions, operatingCost, packageData } = grabPackage(
                "base",
                results[id],
                upgrades[id],
                fuelPrices,
                true
            );

            allEnergies.push(annualEnergyConsumption);
            allEmissions.push(annualEmissions);
            allOpCost.push(operatingCost);
            data.push(packageData);
        }

        // Add upgrade package
        let { annualEnergyConsumption, annualEmissions, operatingCost, packageData } = grabPackage(
            id,
            results[id],
            upgrades[id],
            fuelPrices
        );

        allEnergies.push(annualEnergyConsumption);
        allEmissions.push(annualEmissions);
        allOpCost.push(operatingCost);
        data.push(packageData);
    });

    return {
        key: key,
        name: models[key].name,
        provTerr: models[key].provTerr,
        userName: models[key].userName,
        data: data,
        renoCost: totalRenovationCost,
        maxValues: {
            energy: Math.max(...allEnergies),
            opEmissions: Math.max(...allEmissions),
            opCost: Math.max(...allOpCost),
        },
    };
};

export const getCommunityResults = ({ batchIds, runHistory, runResults }) => {
    // Temporary
    const tempIds = batchIds.slice(0, 1); // duplicate models if multple batches, chekc later

    let tempData = [];

    tempIds.map((batchId) => {
        Object.entries(runResults).forEach(([batchChunkId, { results }]) => {
            if (batchChunkId.includes(batchId)) {
                tempData.push([
                    batchId,
                    {
                        runHistory: runHistory[batchId],
                        runResults: results,
                    },
                ]);
            }
        });
    });

    const allData = Object.fromEntries(tempData);
    let uniqueModels = {};

    tempIds.forEach((batchId) => {
        Object.entries(allData[batchId].runResults).forEach(([combinationId, result]) => {
            let [uniqueModelId, chunkId, packageId] = combinationId.split("-");
            let [baseModelId, facingDirection] = uniqueModelId.split("_");

            const modelDetails = allData[batchId].runHistory.models[baseModelId];

            if (!(uniqueModelId in uniqueModels)) {
                uniqueModels[uniqueModelId] = {
                    name: uniqueModelId.replace(baseModelId, modelDetails.name),
                    provTerr: modelDetails.location.regionId,
                    location: getLabel(
                        "weatherLocation",
                        modelDetails.location.locationId,
                        modelDetails.location.regionId,
                        "v11-12"
                    ),
                    data: [],
                    allEnergies: [],
                };
            }
            uniqueModels[uniqueModelId]["data"].push({
                id: packageId,
                name: packageId,
                energyConsumption: result.aecGJ,
            });

            uniqueModels[uniqueModelId]["allEnergies"].push(result.aecGJ);
        });
    });

    const allResults = Object.entries(uniqueModels).map(([uniqueModelId, value]) => ({
        key: uniqueModelId,
        name: value.name,
        provTerr: value.provTerr,
        userName: value.location,
        data: value.data,
        maxValues: {
            energy: Math.max(...value.allEnergies),
        },
    }));
    return allResults;
};

// Determine whether model has a value to display in graph
export const hasMetricValue = (model, metric) => {
    // metric = energy, opEmissions, opCost, or renoCost
    return metric === "renoCost" ? model.renoCost > 0 : metric in model.maxValues && model.maxValues[metric] > 0;
};

/*****************************************************
    COMMUNITY MODEL FILTERS
*****************************************************/
// Alphabetical sort function by label
const orderByLabel = (a, b) => a.label.localeCompare(b.label);

export const fetchCommFilterOptions = (allModels) => {
    let locations = [];
    let users = [];
    let models = [];

    allModels.forEach((model) => {
        if (!locations.includes(model.provTerr)) locations.push(model.provTerr);

        if (!users.includes(model.userName)) users.push(model.userName);

        models.push(model);
    });

    return {
        locationOpts: locations
            .map((id) => ({
                value: id,
                label: getProvName(id).toUpperCase(),
            }))
            .sort(orderByLabel),
        userOpts: users
            .map((fullName, index) => ({
                value: `User${index}`, // user array index
                label: fullName, // full user name
            }))
            .sort(orderByLabel),
        modelOpts: models.map((model) => ({
            value: model.key, // model ID
            label: model.name, // model name
        })),
    };
};

/*****************************************************
    COMMUNITY ECM INPUT MAP UPGRADES
*****************************************************/
const getCodesToAdd = (fields = {}, codeLib = {}) => {
    const allCodes = extractCodesFromCodeLib(codeLib);

    return Object.entries(fields)
        .filter(([key, value]) => value.codeRef || ["wall", "floor"].includes(key))
        .reduce((acc, [key, value]) => {
            if (value.codeRef) {
                return {
                    ...acc,
                    [value.codeRef]: {
                        codeRef: value.codeRef,
                    },
                };
            } else {
                const subCodes = Object.entries(value).reduce((acc, [subKey, subValue]) => {
                    if (subValue.codeRef) {
                        return {
                            ...acc,
                            [subValue.codeRef]: {
                                codeRef: subValue.codeRef,
                            },
                        };
                    }
                    return acc;
                }, {});

                return {
                    ...acc,
                    ...subCodes,
                };
            }
        }, {});
};

/** Single subtype slice used by shaped ECM branches — never leaves cost[upgradeSubType] as undefined/null/non-object. */
const normalizeEcmCostSlice = (cost, upgradeSubType) => {
    const slice = cost?.[upgradeSubType];
    return {
        [upgradeSubType]: slice != null && typeof slice === "object" ? slice : {},
    };
};

/** Full cost map for default ECM branch: coerce missing or invalid subtype entries to {}. */
const normalizeEcmCostMap = (cost, upgradeSubType) => {
    const src = cost && typeof cost === "object" ? { ...cost } : {};
    Object.keys(src).forEach((k) => {
        if (src[k] == null || typeof src[k] !== "object") {
            src[k] = {};
        }
    });
    if (!(upgradeSubType in src) || src[upgradeSubType] == null || typeof src[upgradeSubType] !== "object") {
        src[upgradeSubType] = {};
    }
    return src;
};

export const getUpgradeToAdd = (
    allUpgrades,
    selectedUpgradePackage,
    ecmInputMap,
    index = 0, // always 0 from function call
    addingNewfromDrawer = false
) => {
    const { upgradeKey, upgradeSubType, upgradeType, parentUpgradeKey, upgradePackageId } = selectedUpgradePackage;

    const selectedUpgrade = addingNewfromDrawer ? allUpgrades : allUpgrades[upgradeKey];

    const { label: labelRaw = "", cost = {}, fields = {}, codes = {} } = selectedUpgrade;
    const labelRecord =
        labelRaw && typeof labelRaw === "object" && !Array.isArray(labelRaw) ? { ...labelRaw } : {};

    const codesToAdd = codes || {};

    const newUpgradeId = upgradeKey ? upgradeKey : `upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${index}`;

    const lastEcmInputComponent = ecmInputMap[upgradeSubType]
        ? Object.entries(ecmInputMap[upgradeSubType])
              .sort((a, b) => a[1].order || 0 - b[1].order || 0)
              .pop()[1]
        : null;

    let orderOfLastComponent = lastEcmInputComponent ? lastEcmInputComponent.order || 0 : 0;

    if (upgradeSubType === "basementSlab") {
        const rowLabel = resolveCompositeEcmRowLabel(labelRecord, upgradeSubType, upgradeType, fields);
        const packageLabel = buildCompositePackageLabelMap(labelRecord, upgradeSubType, rowLabel, upgradeType);
        return {
            [newUpgradeId]: {
                label: rowLabel,
                active: false,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgrade,
                    upgradeType,
                    upgradeSubType,
                    codes: codesToAdd,
                    cost: normalizeEcmCostSlice(cost, upgradeSubType),
                    fields: {
                        ...fields,
                        wall: {},
                    },
                    label: packageLabel,
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                    upgradePackageId, // for communities
                },
            },
        };
    }

    if (upgradeSubType === "basementWall") {
        const rowLabel = resolveCompositeEcmRowLabel(labelRecord, upgradeSubType, upgradeType, fields);
        const packageLabel = buildCompositePackageLabelMap(labelRecord, upgradeSubType, rowLabel, upgradeType);
        return {
            [newUpgradeId]: {
                label: rowLabel,
                active: false,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgrade,
                    upgradeType,
                    upgradeSubType,
                    codes: codesToAdd,
                    cost: normalizeEcmCostSlice(cost, upgradeSubType),
                    fields: {
                        ...fields,
                        floor: {},
                    },
                    label: packageLabel,
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                    upgradePackageId,
                },
            },
        };
    }

    if (upgradeSubType === "dwhr" || upgradeSubType === "primaryHotWater") {
        const rowLabel = resolveCompositeEcmRowLabel(labelRecord, upgradeSubType, upgradeType, fields);
        const packageLabel = buildCompositePackageLabelMap(labelRecord, upgradeSubType, rowLabel, upgradeType);
        return {
            [newUpgradeId]: {
                label: rowLabel,
                active: false,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgrade,
                    upgradeType,
                    upgradeSubType,
                    codes: codesToAdd,
                    cost: normalizeEcmCostSlice(cost, upgradeSubType),
                    fields: {
                        ...fields,
                        secondarySystem: {},
                    },
                    label: packageLabel,
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                    upgradePackageId,
                },
            },
        };
    }

    if (upgradeSubType === "spaceCooling") {
        const rowLabel = resolveCompositeEcmRowLabel(labelRecord, upgradeSubType, upgradeType, fields);
        const packageLabel = buildCompositePackageLabelMap(labelRecord, upgradeSubType, rowLabel, upgradeType);
        return {
            [newUpgradeId]: {
                label: rowLabel,
                active: false,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgrade,
                    upgradeType,
                    upgradeSubType,
                    codes: codesToAdd,
                    cost: normalizeEcmCostSlice(cost, upgradeSubType),
                    fields: {
                        ...fields,
                        heating: {},
                        radiantHeating: {},
                        additionalOpenings: {},
                        supplSystems: {},
                    },
                    label: packageLabel,
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                    upgradePackageId,
                },
            },
        };
    }

    if (upgradeSubType === "spaceHeating") {
        const rowLabel = resolveCompositeEcmRowLabel(labelRecord, upgradeSubType, upgradeType, fields);
        const packageLabel = buildCompositePackageLabelMap(labelRecord, upgradeSubType, rowLabel, upgradeType);
        return {
            [newUpgradeId]: {
                label: rowLabel,
                active: false,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgrade,
                    upgradeType,
                    upgradeSubType,
                    codes: codesToAdd,
                    cost: normalizeEcmCostSlice(cost, upgradeSubType),
                    fields: {
                        ...fields,
                        cooling: {},
                        radiantHeating: {},
                        additionalOpenings: {},
                        supplSystems: {},
                    },
                    label: packageLabel,
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                    upgradePackageId,
                },
            },
        };
    }

    if (upgradeSubType === "secondaryHeating") {
        const rowLabel = resolveCompositeEcmRowLabel(labelRecord, upgradeSubType, upgradeType, fields);
        const packageLabel = buildCompositePackageLabelMap(labelRecord, upgradeSubType, rowLabel, upgradeType);
        return {
            [newUpgradeId]: {
                label: rowLabel,
                active: false,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgrade,
                    upgradeType,
                    upgradeSubType,
                    codes: codesToAdd,
                    cost: normalizeEcmCostSlice(cost, upgradeSubType),
                    fields: {
                        ...fields,
                        cooling: {},
                        radiantHeating: {},
                        additionalOpenings: {},
                        heating: {},
                    },
                    label: packageLabel,
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                    upgradePackageId,
                },
            },
        };
    }

    const compositeForType = COMBINED_H2K_ECM_SUB_TYPES[upgradeType];
    if (compositeForType?.includes(upgradeSubType)) {
        const rowLabel = resolveCompositeEcmRowLabel(labelRecord, upgradeSubType, upgradeType, fields);
        const packageLabel = buildCompositePackageLabelMap(labelRecord, upgradeSubType, rowLabel, upgradeType);
        return {
            [newUpgradeId]: {
                label: rowLabel,
                active: false,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    upgradeType,
                    upgradeSubType,
                    ...selectedUpgrade,
                    codes: codesToAdd,
                    cost: normalizeEcmCostMap(selectedUpgrade?.cost, upgradeSubType),
                    label: packageLabel,
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                    upgradePackageId,
                },
            },
        };
    }

    return {
        [newUpgradeId]: {
            label:
                Object.keys(labelRecord).length > 0
                    ? labelRecord[Object.keys(labelRecord)[0]] || "None"
                    : typeof labelRaw === "string" && labelRaw.trim() !== ""
                      ? labelRaw
                      : "None",
            active: false,
            passed: null,
            individualSavings: null,
            noChange: false,
            order: orderOfLastComponent + 1,
            packageValues: {
                upgradeType,
                upgradeSubType,
                ...selectedUpgrade,
                codes: codesToAdd,
                cost: normalizeEcmCostMap(selectedUpgrade?.cost, upgradeSubType),
                selectedComponents: ["*"],
                parentUpgradeKey,
                upgradePackageId,
            },
        },
    };
};

export const getUpgradesToAdd = (allUpgradesToAdd, ecmInputMap) => {
    const upgradesToAdd = [];

    Object.entries(allUpgradesToAdd).forEach(([key, value], index) => {
        if (value.upgradeType === "baseLoads") return;

        const ecmSubTypes = collectEcmSubTypesForPackageUpgrade(value);

        if (ecmSubTypes.length >= 1) {
            ecmSubTypes.forEach((upgradeSubType) => {
                const selectedUpgradePackage = {
                    upgradeKey: key,
                    upgradeSubType,
                    upgradeType: value.upgradeType,
                    parentUpgradeKey: null,
                    upgradePackageId: value.upgradePackageId,
                };

                const upgradeToAdd = getUpgradeToAdd(
                    allUpgradesToAdd,
                    selectedUpgradePackage,
                    ecmInputMap,
                    index,
                    false
                );

                upgradesToAdd.push({ ...upgradeToAdd });
            });
        }
    });

    return upgradesToAdd;
};

/* Return all model components of type "upgradeType" */
const getAllComponentsOfType = (components, upgradeH2kType, upgradeType) => {
    let allComponentsOfType = {};

    if (!components) return allComponentsOfType;

    // Check for "upgradeType" components present in the model components or their subcomponents
    Object.entries(components).forEach(([componentType, componentsList]) => {
        Object.entries(componentsList).forEach(([mainComponentKey, mainComponentValue]) => {
            // Find main component (wall, ceiling, basement, crawlspace, slab, exposed floor)
            if (upgradeH2kType === componentType) {
                allComponentsOfType = {
                    ...allComponentsOfType,
                    [mainComponentKey]: {
                        pathTo: `${componentType}.${mainComponentKey}`,
                    },
                };
            }

            // Find subcomponent (door, window, floor header)
            if (["floorHeader", "door", "window"].includes(upgradeH2kType)) {
                if (!mainComponentValue.subcomponents) return;

                Object.entries(mainComponentValue.subcomponents).forEach(([subComponentType, subComponentsList]) => {
                    Object.entries(subComponentsList).forEach(([subComponentKey, subComponentsValue]) => {
                        if (upgradeH2kType === subComponentType) {
                            // Ignore windows not on ceilings when searching for skylights
                            if (upgradeType === "skylights" && componentType !== "ceiling") return;

                            // Ignore windows on ceilings when searching for windows
                            if (upgradeType === "window" && componentType === "ceiling") return;

                            // Add subcomponents (doors, windows, floor headers)
                            allComponentsOfType = {
                                ...allComponentsOfType,
                                [subComponentKey]: {
                                    pathTo: `${componentType}.${mainComponentKey}.subcomponents.${subComponentType}.${subComponentKey}`,
                                },
                            };
                        } else if (upgradeType === "window" && subComponentType === "door") {
                            // Check for windows on a door
                            Object.entries(subComponentsValue.subcomponents).forEach(
                                ([nestedComponentType, nestedComponentList]) => {
                                    Object.entries(nestedComponentList).forEach(
                                        ([nestedComponentKey, nestedComponentValue]) => {
                                            allComponentsOfType = {
                                                ...allComponentsOfType,
                                                [nestedComponentKey]: {
                                                    pathTo: `${componentType}.${mainComponentKey}.subcomponents.${subComponentType}.${subComponentKey}.subcomponents.${nestedComponentType}.${nestedComponentKey}`,
                                                },
                                            };
                                        }
                                    );
                                }
                            );
                        }
                    });
                });
            }
        });
    });

    return allComponentsOfType;
};

export const getSelectedComponents = (upgradeType, upgradeH2kType, components) => {
    const selectedComponents = [];

    const componentsType = getAllComponentsOfType(components, upgradeH2kType, upgradeType);

    if (!componentsType) return selectedComponents;

    Object.entries(componentsType).forEach(([_, { pathTo }]) => {
        selectedComponents.push(pathTo);
    });

    return selectedComponents;
};

export const getCommSelectedComponents = (upgradeType, upgradeH2kType, componentsFromCommunity) => {
    const allSelectedComponents = Object.entries(componentsFromCommunity).map(([modelId, modelComponents]) => {
        return [modelId, getSelectedComponents(upgradeType, upgradeH2kType, modelComponents)];
    });
    return Object.fromEntries(allSelectedComponents);
};

/*****************************************************
    UTILITY FUNCTIONS
*****************************************************/

const calcIndividualFuelCostCommunity = (amount, unitCost, monthCost, rebate, tax) => {
    if (amount === 0) {
        return 0;
    }

    unitCost = isNaN(unitCost) ? 0 : unitCost;
    monthCost = isNaN(monthCost) ? 0 : monthCost;
    rebate = isNaN(rebate) ? 0 : rebate;
    tax = isNaN(tax) ? 0 : tax;

    const fixedCost = amount > 0 ? monthCost * 12 : 0;
    const initCost = amount * unitCost + fixedCost;
    const rebateAmount = initCost * (rebate / 100);
    const taxAmount = initCost * (tax / 100);

    return initCost + taxAmount - rebateAmount || 0;
};

export const calcOperatingCostsCommunity = (resultsObj = {}, fuelPrices = {}) => {
    const {
        elecGJ: totalElecGJ = 0,
        ngGJ: totalNgGJ = 0,
        propaneGJ: totalPropaneGJ = 0,
        oilGJ: totalOilGJ = 0,
        woodGJ: totalWoodGJ = 0,
        pvGJ: totalPVGJ = 0,
    } = resultsObj || {};

    //Check for price info, handle differently if none is present
    // const elecDefault = 0.16;
    // const ngDefault = 0.3;
    // const propDefault = 1.28;
    // const oilDefault = 2.31;
    // const woodDefault = 460;
    const elecDefault = 0;
    const ngDefault = 0;
    const propDefault = 0;
    const oilDefault = 0;
    const woodDefault = 0;
    let {
        electricity: {
            dollarsPerUnit: elecCostPerUnit = elecDefault,
            dollarsPerMonth: elecPerMonth = 0,
            preTaxRebate: elecRebate = 0,
            tax: elecTax = 0,
        } = {},
        naturalGas: {
            dollarsPerUnit: ngCostPerUnit = ngDefault,
            dollarsPerMonth: ngPerMonth = 0,
            preTaxRebate: ngRebate = 0,
            tax: ngTax = 0,
        } = {},
        propane: {
            dollarsPerUnit: propCostPerUnit = propDefault,
            dollarsPerMonth: propPerMonth = 0,
            preTaxRebate: propRebate = 0,
            tax: propTax = 0,
        } = {},
        oil: {
            dollarsPerUnit: oilCostPerUnit = oilDefault,
            dollarsPerMonth: oilPerMonth = 0,
            preTaxRebate: oilRebate = 0,
            tax: oilTax = 0,
        } = {},
        wood: {
            dollarsPerUnit: woodCostPerUnit = woodDefault,
            dollarsPerMonth: woodPerMonth = 0,
            preTaxRebate: woodRebate = 0,
            tax: woodTax = 0,
        } = {},
    } = fuelPrices;

    //NaNs don't get destructured as if they're undefined, so we have to impose the
    elecCostPerUnit = isNaN(elecCostPerUnit) ? elecDefault : elecCostPerUnit;
    ngCostPerUnit = isNaN(ngCostPerUnit) ? ngDefault : ngCostPerUnit;
    propCostPerUnit = isNaN(propCostPerUnit) ? propDefault : propCostPerUnit;
    oilCostPerUnit = isNaN(oilCostPerUnit) ? oilDefault : oilCostPerUnit;
    woodCostPerUnit = isNaN(woodCostPerUnit) ? woodDefault : woodCostPerUnit;

    const elecKwh = totalElecGJ / energyConversion.elecGJperkWh;
    const pvKWh = Math.min(elecKwh, totalPVGJ / energyConversion.elecGJperkWh);
    const ngM3 = totalNgGJ / energyConversion.ngGJperm3;
    const propL = totalPropaneGJ / energyConversion.propaneGJperL;
    const oilL = totalOilGJ / energyConversion.oilGJperL;
    const woodCord = totalWoodGJ / energyConversion.woodGJperCord;

    const elecCost = calcIndividualFuelCostCommunity(elecKwh, elecCostPerUnit, elecPerMonth, elecRebate, elecTax);
    const pvCostSavings = calcIndividualFuelCostCommunity(pvKWh, elecCostPerUnit, elecPerMonth, elecRebate, elecTax);
    const ngCost = calcIndividualFuelCostCommunity(ngM3, ngCostPerUnit, ngPerMonth, ngRebate, ngTax);
    const propCost = calcIndividualFuelCostCommunity(propL, propCostPerUnit, propPerMonth, propRebate, propTax);
    const oilCost = calcIndividualFuelCostCommunity(oilL, oilCostPerUnit, oilPerMonth, oilRebate, oilTax);
    const woodCost = calcIndividualFuelCostCommunity(woodCord, woodCostPerUnit, woodPerMonth, woodRebate, woodTax);

    return {
        electricity: elecCost,
        naturalGas: ngCost,
        propane: propCost,
        oil: oilCost,
        wood: woodCost,
        total: elecCost + ngCost + propCost + oilCost + woodCost,
        totalWithPV: elecCost + ngCost + propCost + oilCost + woodCost - pvCostSavings,
    };
};

export const checkIsSimulationRunning = (runHistory) => Object.values(runHistory).some((run) => !run.complete);

export const getAllModelsInTheRun = ({ runHistory }) => {
    const allModels = [];

    Object.entries(runHistory).forEach(([_, batchDetails]) => {
        const { models } = batchDetails;

        Object.entries(models).forEach(([modelId, { name }]) => {
            if (!allModels.some((model) => model.value === modelId)) {
                allModels.push({
                    value: modelId,
                    label: name,
                });
            }
        });
    });

    return allModels;
};

export const getResults = (
    selectedModels,
    { runHistory, runResults },
    fuelPrices = {},
    communityModelCountsMap = {}
) => {
    const resultsToReturn = {};

    const upgradePackagesNames = {};

    Object.entries(runHistory).forEach(([historyId, historyDetails]) => {
        const { upgradePackages } = historyDetails;

        Object.entries(upgradePackages).forEach(([upgradePackageId, upgradePackageDetails]) => {
            upgradePackagesNames[upgradePackageId] = {
                name: upgradePackageDetails.name,
                order: upgradePackageDetails.ui?.displayOrder,
            };
        });
    });

    Object.entries(runResults).forEach(([batchId, batchDetails]) => {
        const { results } = batchDetails;

        Object.entries(results).forEach(([resultsId, result]) => {
            const [uniqueModelId, chunkId, combinationId] = resultsId.split("-");
            const [baseModelId, facingDirection] = uniqueModelId.split("_");

            const modelResultMultiplier = Math.max(0, communityModelCountsMap[uniqueModelId] || 1);

            let { upgradePackageId, listOfUpgrades } = result;

            let totalOperatingCost = 0;
            let totalOperatingCostWithPV = 0;
            let electricityCost = 0;
            let naturalGasCost = 0;
            let propaneCost = 0;
            let oilCost = 0;
            let woodCost = 0;
            if (!isEmpty(fuelPrices)) {
                const operatingCostObject = calcOperatingCostsCommunity(result, fuelPrices);

                totalOperatingCost = operatingCostObject.total;
                totalOperatingCostWithPV = operatingCostObject.totalWithPV;
                electricityCost = operatingCostObject.electricity;
                naturalGasCost = operatingCostObject.naturalGas;
                propaneCost = operatingCostObject.propane;
                oilCost = operatingCostObject.oil;
                woodCost = operatingCostObject.wood;
            }

            if (selectedModels.includes(baseModelId)) {
                if (listOfUpgrades === "") upgradePackageId = "base";

                if (!resultsToReturn[upgradePackageId]) {
                    resultsToReturn[upgradePackageId] = [];
                }

                resultsToReturn[upgradePackageId].push({
                    ...result,
                    modelResultMultiplier: modelResultMultiplier,
                    modelId: baseModelId,
                    facingDirection: facingDirection,
                    totalOperatingCost: totalOperatingCost || 0,
                    totalOperatingCostWithPV: totalOperatingCostWithPV || 0,
                    electricityCost: electricityCost || 0,
                    naturalGasCost: naturalGasCost || 0,
                    propaneCost: propaneCost || 0,
                    oilCost: oilCost || 0,
                    woodCost: woodCost || 0,
                    upgradePackageName:
                        listOfUpgrades === "" ? "Base Case" : upgradePackagesNames[upgradePackageId].name,
                    upgradePackageOrder: listOfUpgrades === "" ? 0 : upgradePackagesNames[upgradePackageId].order,
                });
            }
        });
    });

    return Object.fromEntries(
        Object.entries(resultsToReturn).sort((a, b) => a[1].upgradePackageOrder - b[1].upgradePackageOrder)
    );
};

// Return total for each upgrade package in these categories:
// electricity - elecGJ, naturalGas - ngGJ,  totalEnergyWithoutPV - aecGJ,
// ghgEmissions - totalEmissionsTCO2, electricityCost - 0 for now, naturalGasCost - 0 for now
// For graph it should be an array of objects with the following structure: [{ packageName, packageId, electricity, naturalGas, totalEnergyWithoutPV, ghgEmissions, electricityCost, naturalGasCost }], all values should be total for all same packageId results
export const organizeDataForDisplay = (results, allPackages, applyModelResultMultiplier = false) => {
    const data = [];

    Object.entries(results).forEach(([packageId, packageResults]) => {
        const totalElectricity = packageResults.reduce(
            (acc, result) =>
                acc + (result.elecGJ || 0) * (applyModelResultMultiplier ? result.modelResultMultiplier || 1 : 1),
            0
        );
        const totalNaturalGas = packageResults.reduce(
            (acc, result) =>
                acc + (result.ngGJ || 0) * (applyModelResultMultiplier ? result.modelResultMultiplier || 1 : 1),
            0
        );
        const totalEnergyWithoutPV = packageResults.reduce(
            (acc, result) =>
                acc + (result.aecGJ || 0) * (applyModelResultMultiplier ? result.modelResultMultiplier || 1 : 1),
            0
        );
        const totalEnergyWithPV = packageResults.reduce(
            (acc, result) =>
                acc + (result.netGJ || 0) * (applyModelResultMultiplier ? result.modelResultMultiplier || 1 : 1),
            0
        );
        // const totalPV = packageResults.reduce((acc, result) => acc + (result.pvGJ || 0), 0);
        const totalEmissionsTCO2 = packageResults.reduce(
            (acc, result) =>
                acc +
                (result.totalEmissionsTCO2 || 0) * (applyModelResultMultiplier ? result.modelResultMultiplier || 1 : 1),
            0
        );
        const totalElectricityCost = packageResults.reduce(
            (acc, result) =>
                acc +
                (result.electricityCost || 0) * (applyModelResultMultiplier ? result.modelResultMultiplier || 1 : 1),
            0
        );
        const totalNaturalGasCost = packageResults.reduce(
            (acc, result) =>
                acc +
                (result.naturalGasCost || 0) * (applyModelResultMultiplier ? result.modelResultMultiplier || 1 : 1),
            0
        );

        const electricityKWH = parseFloat(
            convertUnit({ value: totalElectricity, type: "energy", inputUnit: "GJ", outputUnit: "kWh" }).toFixed(2)
        );
        const naturalGasKWH = parseFloat(
            convertUnit({ value: totalNaturalGas, type: "energy", inputUnit: "GJ", outputUnit: "kWh" }).toFixed(2)
        );
        const naturalGasM3 = parseFloat((totalNaturalGas * 26.81).toFixed(2));

        const { name = "", archived = false, ui: { displayOrder = "" } = {} } = allPackages[packageId] || {};

        if (!archived) {
            data.push({
                packageName: packageId === "base" ? "Base Case" : name || packageResults[0].upgradePackageName,
                packageOrder: packageId === "base" ? -1 : displayOrder || packageResults[0].upgradePackageOrder,
                packageId: packageId,
                elecKWH: electricityKWH,
                elecGJ: parseFloat(totalElectricity.toFixed(2)),
                ngKWH: naturalGasKWH,
                ngGJ: parseFloat(totalNaturalGas.toFixed(2)),
                ngM3: naturalGasM3,
                aecGJ: parseFloat(totalEnergyWithoutPV.toFixed(2)),
                netGJ: parseFloat(totalEnergyWithPV.toFixed(2)),
                totalEmissionsTCO2: parseFloat(totalEmissionsTCO2.toFixed(2)),
                electricityCost: parseFloat(totalElectricityCost.toFixed(2)),
                naturalGasCost: parseFloat(totalNaturalGasCost.toFixed(2)),
            });
        }
    });

    return data.sort((a, b) => {
        // Base case should always be first
        if (a.packageId === "base") return -1;
        if (b.packageId === "base") return 1;
        // Otherwise sort by packageOrder descending
        return a.packageOrder - b.packageOrder;
    });
};

export const getMaxValueForGraphMetric = (organizedData, graphMetric) => {
    let maxValue = 0;

    organizedData.forEach((result) => {
        if (graphMetric === "energy") {
            maxValue = Math.max(maxValue, result.elecGJ, result.ngGJ, result.aecGJ);
        }

        if (graphMetric === "opEmissions") {
            maxValue = Math.max(maxValue, result.totalEmissionsTCO2);
        }

        if (graphMetric === "opCost") {
            maxValue = Math.max(maxValue, result.electricityCost, result.naturalGasCost);
        }
    });

    return maxValue * 1.5;
};

export const getAllUpgradePackagesInTheRun = (
    { runHistory = {} },
    modelUpgradesRun = {},
    selectedModels = [],
    allPackages = {},
    initialLoad = false
) => {
    const upgradePackagesToReturn = [];
    const allUpgradePackages = [];

    Object.entries(runHistory).forEach(([batchId, batchDetails]) => {
        const { upgradePackages } = batchDetails;

        Object.entries(upgradePackages).forEach(([upgradePackageId, upgradePackageDetails]) => {
            if (
                !allUpgradePackages.some((upgradePackage) => upgradePackage.value === upgradePackageId) &&
                !allPackages[upgradePackageId]?.archived
            )
                allUpgradePackages.push({
                    value: upgradePackageId,
                    label: allPackages[upgradePackageId]?.name || upgradePackageDetails.name,
                });
        });
    });

    selectedModels.forEach((modelId) => {
        if (modelUpgradesRun[modelId]) {
            allUpgradePackages.forEach((upgradePackage) => {
                if (
                    modelUpgradesRun[modelId][upgradePackage.value] &&
                    !upgradePackagesToReturn.some(
                        (upgradePackageToCheck) => upgradePackageToCheck.value === upgradePackage.value
                    )
                ) {
                    upgradePackagesToReturn.push(upgradePackage);
                }
            });
        }
    });

    return upgradePackagesToReturn;
};
/*
 Filter data => remove everything that doesn't have selected upgrade package
 Combine by the same upgrade fomr upgadeId, and count average percentSaving
 start of the next upgrade is the end of the previous upgrade
 return the data in the following format:
  [{
    upgradeId: "",
    upgradeType: "",
    startEnergy: 0,
    startEmissions: 0,
    endEnergy: 0,
    endEmissions: 0,
    averagePercentEnergySaving: 0,
    averagePercentEmissionSaving: 0,
    packagePercentEnergySavings: 0,
    packagePercentEmissionSavings: 0,
    upgradePercentEnergySavings: 0,
    upgradePercentEmissionSavings: 0,
    divideTotalBy: 1,
  }]

  start and end in the beginning are zero, 
  because we're going to sort 
  and calculate the correct start and end by correct order of upgrades after
*/

/**
 * Rows from `runResultsByMeasure`: prefer `upgradeIds` for stable type + id (labels can drift from ECM copy).
 * Falls back to `upgradesPresent` (id + type from API), then `listOfUpgrades` when ids string is missing.
 */
const getResultMeasureSegments = (listOfUpgrades, upgradeIds, upgradesPresent) => {
    const fromIds = parseSemicolonUpgradePairs(upgradeIds);
    if (fromIds.length > 0) {
        return fromIds.map(({ upgradeType, value }) => ({
            upgradeType,
            upgradeId: value,
        }));
    }

    if (upgradesPresent && typeof upgradesPresent === "object" && !isEmpty(upgradesPresent)) {
        return Object.entries(upgradesPresent).map(([upgradeType, v]) => ({
            upgradeType,
            upgradeId: v?.id ?? null,
        }));
    }

    return parseSemicolonUpgradePairs(listOfUpgrades).map(({ upgradeType, value }) => ({
        upgradeType,
        upgradeId: null,
    }));
};

const WF_RUN_GAPS_MAX_SAMPLES = 35;
/** Must match {@link wfSnapshotByMeasureRun} string slice so segment-count heuristics can ignore truncation false positives. */
const WF_RUN_GAPS_SAMPLE_TEXT_MAX = 240;

const wfRunGapsBucket = () => ({ count: 0, samples: [] });

const wfRunGapsAdd = (bucket, entry, maxSamples = WF_RUN_GAPS_MAX_SAMPLES) => {
    bucket.count += 1;
    if (bucket.samples.length < maxSamples) {
        bucket.samples.push(entry);
    }
};

const wfSnapshotPackageRun = (resultsId, result, uniqueModelId, baseModelId, extra = {}) => {
    const {
        upgradePackageId = "",
        aecGJ = 0,
        totalEmissionsTCO2 = 0,
        percentSavings = 0,
        percentEmissionSavings = 0,
        result: resultStatus = "",
    } = result;
    return {
        resultsId,
        uniqueModelId,
        baseModelId,
        upgradePackageId,
        resultStatus,
        aecGJ,
        netOrSiteGJ: siteEnergyGJForPackageWeighting(result),
        totalEmissionsTCO2,
        percentSavings,
        percentEmissionSavings,
        ...extra,
    };
};

const wfSnapshotByMeasureRun = (resultsId, result, uniqueModelId, baseModelId, extra = {}) => {
    const {
        upgradePackageId = "",
        listOfUpgrades = "",
        upgradeIds = "",
        specialRunType = "",
        percentSavings: percentEnergySavings = 0,
        percentEmissionSavings = 0,
        aecGJ = 0,
        totalEmissionsTCO2 = 0,
        result: resultStatus = "",
    } = result;
    return {
        resultsId,
        uniqueModelId,
        baseModelId,
        upgradePackageId,
        specialRunType,
        resultStatus,
        aecGJ,
        netOrSiteGJ: siteEnergyGJForPackageWeighting(result),
        totalEmissionsTCO2,
        percentEnergySavings,
        percentEmissionSavings,
        listOfUpgrades: typeof listOfUpgrades === "string" ? listOfUpgrades.slice(0, WF_RUN_GAPS_SAMPLE_TEXT_MAX) : listOfUpgrades,
        upgradeIds: typeof upgradeIds === "string" ? upgradeIds.slice(0, WF_RUN_GAPS_SAMPLE_TEXT_MAX) : upgradeIds,
        ...extra,
    };
};

const splitPipeUpgradeList = (s) => {
    if (s == null || typeof s !== "string") {
        return [];
    }
    return s
        .split(";")
        .map((x) => x.trim())
        .filter(Boolean);
};

const diagnosticMatchesFocusModel = (baseModelId, uniqueModelId, focusIds) => {
    if (!focusIds || focusIds.length === 0) {
        return false;
    }
    if (baseModelId && focusIds.includes(baseModelId)) {
        return true;
    }
    if (!uniqueModelId) {
        return false;
    }
    const prefix = uniqueModelId.split("_")[0];
    return focusIds.includes(prefix);
};

const WATERFALL_PROBE_TEXT_MAX = 2000;

/** Returns trimmed value after `category=>` in a semicolon-separated h2k string, or null. */
const parsePipeCategoryTail = (str, category) => {
    if (typeof str !== "string" || !category) {
        return null;
    }
    const re = new RegExp(`${category}\\s*=>\\s*([^;]+)`, "i");
    const m = str.match(re);
    return m ? m[1].trim() : null;
};

const byMeasureRowMatchesSingleSegmentProbe = (segments, upgradeIds, probe) => {
    if (!probe?.upgradeId || !probe?.upgradeType) {
        return false;
    }
    const { upgradeType, upgradeId, requireSingleSegmentOnly } = probe;
    if (requireSingleSegmentOnly && segments.length !== 1) {
        return false;
    }
    if (!requireSingleSegmentOnly && segments.length === 0) {
        return false;
    }
    const seg = segments[0];
    if (!seg || seg.upgradeType !== upgradeType) {
        return false;
    }
    if (seg.upgradeId === upgradeId) {
        return true;
    }
    if (typeof upgradeIds === "string" && upgradeIds.includes(`${upgradeType}=>${upgradeId}`)) {
        return true;
    }
    return false;
};

/**
 * Scans raw `runResultsByMeasure` for a single-segment row (e.g. spaceCooling only + ECM id) on focus models.
 * @returns {object|null} `{ probeSpec, hits, notFound, hints }` or `null` when probe/focus disabled.
 */
const collectByMeasureSingleSegmentProbe = ({
    runResultsByMeasure,
    selectedUpgradePackage,
    selectedModels,
    communityModelCountsMap,
    applyModelResultMultiplier,
    baseSiteGJWeighted,
    baseEmissions,
}) => {
    const probe = WATERFALL_DIAGNOSTIC_PROBE;
    const focusIds = WATERFALL_DIAGNOSTIC_FOCUS_BASE_MODEL_IDS;
    if (!probe?.upgradeId || !focusIds || focusIds.length === 0) {
        return null;
    }

    const hits = [];

    runResultsByMeasure?.forEach(({ results }) => {
        if (!results) {
            return;
        }
        Object.entries(results).forEach(([resultsId, result]) => {
            const [uniqueModelId] = resultsId.split("-");
            const [baseModelId] = uniqueModelId.split("_");
            const modelResultMultiplier = Math.max(0, communityModelCountsMap[uniqueModelId] || 1);

            if (!selectedModels.includes(baseModelId)) {
                return;
            }
            if (!diagnosticMatchesFocusModel(baseModelId, uniqueModelId, focusIds)) {
                return;
            }

            const {
                upgradePackageId = "",
                listOfUpgrades = "",
                upgradeIds = "",
                upgradesPresent = null,
                specialRunType = "",
                percentSavings: percentEnergySavingsResult = 0,
                percentEmissionSavings: percentEmissionSavingsResult = 0,
                result: resultStatus = "",
            } = result;

            if (upgradePackageId === "base" || upgradePackageId !== selectedUpgradePackage) {
                return;
            }

            const segments = getResultMeasureSegments(listOfUpgrades, upgradeIds, upgradesPresent);
            if (!byMeasureRowMatchesSingleSegmentProbe(segments, upgradeIds, probe)) {
                return;
            }

            const abs = auditAbsoluteSnapshotForOrientation(
                uniqueModelId,
                result,
                baseSiteGJWeighted,
                baseEmissions,
                applyModelResultMultiplier,
                modelResultMultiplier
            );
            const baseGJ = abs.baselineSiteGJ;
            const scenGJ = abs.scenarioSiteOrNetGJ;
            const impliedE = baseGJ > 0 && Number.isFinite(scenGJ) ? 100 * (1 - scenGJ / baseGJ) : null;
            const baseEm = abs.baselineEmissionsTCO2;
            const scenEm = abs.scenarioEmissionsTCO2;
            const impliedEm = baseEm > 0 && Number.isFinite(scenEm) ? 100 * (1 - scenEm / baseEm) : null;

            const listTail = parsePipeCategoryTail(listOfUpgrades, probe.upgradeType);
            const idTail = parsePipeCategoryTail(upgradeIds, probe.upgradeType);
            const labelVsIdMismatch =
                listTail != null &&
                idTail != null &&
                /^none$/i.test(listTail) &&
                !/^none$/i.test(idTail) &&
                idTail.length > 0;

            hits.push({
                resultsId,
                uniqueModelId,
                baseModelId,
                specialRunType: specialRunType || "",
                resultStatus,
                percentEnergySavings: percentEnergySavingsResult,
                percentEmissionSavings: percentEmissionSavingsResult,
                impliedEnergyPctFromGj: impliedE,
                impliedEmissionPctFromTco2: impliedEm,
                segmentCount: segments.length,
                labelVsIdMismatch,
                listOfUpgradesLabelTail: listTail ?? "",
                upgradeIdsCategoryTail: idTail ?? "",
                ...abs,
                listOfUpgrades: truncateAuditText(listOfUpgrades, WATERFALL_PROBE_TEXT_MAX),
                upgradeIds: truncateAuditText(upgradeIds, WATERFALL_PROBE_TEXT_MAX),
            });
        });
    });

    const notFound = hits.length === 0;
    const hints = notFound
        ? [
              "No by-measure row matched: single segment only, selected package, focus base model, and upgradeIds/type as configured.",
              "If the combo generator never emits a lone spaceCooling line, the run will not exist in Firestore.",
              "Rows in excludedWrongPackage are other packages — expand selected package or re-run community batch.",
          ]
        : undefined;

    return {
        probeSpec: { ...probe },
        hits,
        notFound,
        hints,
    };
};

/**
 * Heuristics for debugging waterfall / by-measure rows: list/ids alignment, label "None", implied % from GJ.
 * Attached to `runGaps.diagnosticFocus` when {@link WATERFALL_DIAGNOSTIC_FOCUS_BASE_MODEL_IDS} is non-empty.
 */
const buildWaterfallRunDiagnostics = (
    runGaps,
    focusBaseModelIds = WATERFALL_DIAGNOSTIC_FOCUS_BASE_MODEL_IDS,
    byMeasureProbeResult = null
) => {
    if (!runGaps || !focusBaseModelIds || focusBaseModelIds.length === 0) {
        return null;
    }

    const interpretation = [
        "Waterfall bars are package-anchored: per-measure GJ (or tCO₂) deltas are scaled to sum to the whole-package community total (sequence-agnostic).",
        "listOfUpgrades / upgradeIds length mismatch suggests h2kInfo pairing risk in getSnapParametricResults (index-aligned reduce).",
        "spaceCooling=>None in the string while GJ/tCO₂ look like a full HP run may be a label-only issue (see packageValues in Firestore).",
        "Large Δ(API % vs implied from baseline/scenario GJ) can mean net vs aec basis, missing baseline, or API % not tied to site GJ.",
        "Combined specialRunType by-measure rows are excluded from the merge (see run gaps) to avoid double-counting single-measure runs.",
        "Run-gap samples truncate listOfUpgrades/upgradeIds to the same cap as wfSnapshotByMeasureRun — segment-count mismatch is skipped when both strings hit that cap (avoids false positives).",
        "Single-measure probe (below): full raw row scan for one ECM on focus models — see hits / notFound.",
    ];

    const checkpointContributions = [];
    const trail = runGaps.checkpointAuditTrail;
    if (trail?.tiers) {
        for (const [tierKey, tier] of Object.entries(trail.tiers)) {
            for (const c of tier.contributions || []) {
                if (!diagnosticMatchesFocusModel(c.baseModelId, c.uniqueModelId, focusBaseModelIds)) {
                    continue;
                }
                const baseGJ = Number(c.baselineSiteGJ);
                const scenGJ = Number(c.scenarioSiteOrNetGJ);
                const impliedE =
                    baseGJ > 0 && Number.isFinite(scenGJ) ? 100 * (1 - scenGJ / baseGJ) : null;
                const reportedE = Number(c.percentEnergySavings);
                const deltaE =
                    impliedE != null && Number.isFinite(reportedE) ? reportedE - impliedE : null;

                const baseEm = Number(c.baselineEmissionsTCO2);
                const scenEm = Number(c.scenarioEmissionsTCO2);
                const impliedEm = baseEm > 0 && Number.isFinite(scenEm) ? 100 * (1 - scenEm / baseEm) : null;
                const reportedEm = Number(c.percentEmissionSavings);
                const deltaEm =
                    impliedEm != null && Number.isFinite(reportedEm) ? reportedEm - impliedEm : null;

                const listStr = typeof c.listOfUpgrades === "string" ? c.listOfUpgrades : "";
                checkpointContributions.push({
                    tierKey,
                    uniqueModelId: c.uniqueModelId,
                    source: c.source,
                    specialRunType: c.specialRunType,
                    reportedEnergyPct: reportedE,
                    impliedEnergyPctFromGj: impliedE,
                    deltaApiMinusImpliedEnergy: deltaE,
                    reportedEmissionPct: reportedEm,
                    impliedEmissionPctFromTco2: impliedEm,
                    deltaApiMinusImpliedEmissions: deltaEm,
                    flags: {
                        spaceCoolingLabelNone: /spaceCooling\s*=>\s*None\b/i.test(listStr),
                        inheritedCascade: Boolean(c.inheritedFromCheckpoint),
                        largeEnergyDelta:
                            deltaE != null && Number.isFinite(deltaE) && Math.abs(deltaE) > 2.5,
                        largeEmissionsDelta:
                            deltaEm != null && Number.isFinite(deltaEm) && Math.abs(deltaEm) > 2.5,
                    },
                });
            }
        }
    }

    const runSampleFindings = [];
    const scanSamples = (groups, streamLabel) => {
        if (!groups || typeof groups !== "object") {
            return;
        }
        for (const [bucketKey, bucket] of Object.entries(groups)) {
            const samples = bucket?.samples || [];
            samples.forEach((s, idx) => {
                if (!diagnosticMatchesFocusModel(s.baseModelId, s.uniqueModelId, focusBaseModelIds)) {
                    return;
                }
                const listSeg = splitPipeUpgradeList(s.listOfUpgrades);
                const idSeg = splitPipeUpgradeList(s.upgradeIds);
                if (s.listOfUpgrades != null || s.upgradeIds != null) {
                    const lu = s.listOfUpgrades;
                    const ui = s.upgradeIds;
                    const truncatedBoth =
                        typeof lu === "string" &&
                        typeof ui === "string" &&
                        lu.length === WF_RUN_GAPS_SAMPLE_TEXT_MAX &&
                        ui.length === WF_RUN_GAPS_SAMPLE_TEXT_MAX;
                    if (listSeg.length !== idSeg.length && !truncatedBoth) {
                        runSampleFindings.push({
                            stream: streamLabel,
                            bucket: bucketKey,
                            sampleIndex: idx,
                            uniqueModelId: s.uniqueModelId,
                            reason: "listOfUpgrades_segment_count !== upgradeIds_segment_count",
                            listSegments: listSeg.length,
                            idSegments: idSeg.length,
                            listOfUpgradesTruncatedSample: s.listOfUpgrades,
                            upgradeIdsTruncatedSample: s.upgradeIds,
                        });
                    }
                }
                const lstr = String(s.listOfUpgrades || "");
                if (/spaceCooling\s*=>\s*None\b/i.test(lstr)) {
                    runSampleFindings.push({
                        stream: streamLabel,
                        bucket: bucketKey,
                        sampleIndex: idx,
                        uniqueModelId: s.uniqueModelId,
                        reason: "listOfUpgrades contains spaceCooling=>None (check label vs packageValues in ecmInputMap)",
                    });
                }
            });
        }
    };

    scanSamples(runGaps.packageRuns, "package_runResults");
    scanSamples(runGaps.byMeasureRuns, "byMeasure_runResultsByMeasure");

    const cc = runGaps.crossCheck || {};
    const missingPkg = cc.missingPackageRowForBaselineModel || [];
    const focusMissingPackage = missingPkg.filter((uid) =>
        focusBaseModelIds.some((id) => uid === id || uid.startsWith(`${id}_`))
    );

    return {
        focusBaseModelIds: [...focusBaseModelIds],
        interpretation,
        checkpointContributions,
        runSampleFindings,
        crossChecksForFocus: {
            missingPackageRowOrientations: focusMissingPackage,
            packageRowButNoBaselineEmissions: (cc.selectedPackageRowButNoBaselineEmissionsRecord || []).filter((uid) =>
                focusBaseModelIds.some((id) => uid === id || uid.startsWith(`${id}_`))
            ),
            packageRowButNoBaselineSiteGJ: (cc.selectedPackageRowButNoBaselineSiteGJRecord || []).filter((uid) =>
                focusBaseModelIds.some((id) => uid === id || uid.startsWith(`${id}_`))
            ),
        },
        ...(byMeasureProbeResult != null ? { singleMeasureProbe: byMeasureProbeResult } : {}),
    };
};

const createWaterfallRunGapsCollector = () => ({
    packageRuns: {
        fail: wfRunGapsBucket(),
        skippedNoAecGjAndNoPercentSavings: wfRunGapsBucket(),
        baselinePackageRow: wfRunGapsBucket(),
        wrongOrOtherPackage: wfRunGapsBucket(),
        includedInTotalCheckpoint: wfRunGapsBucket(),
    },
    byMeasureRuns: {
        fail: wfRunGapsBucket(),
        skippedNoAecGjAndNoPercentSavings: wfRunGapsBucket(),
        excludedBasePackage: wfRunGapsBucket(),
        excludedWrongPackage: wfRunGapsBucket(),
        noMeasureSegmentsParsed: wfRunGapsBucket(),
        includedInWaterfallMerge: wfRunGapsBucket(),
        includedSpecialCheckpointOnly: wfRunGapsBucket(),
    },
    rowCounts: {
        packageResultsNotSelectedModel: 0,
        packageResultsSelectedModel: 0,
        byMeasureResultsNotSelectedModel: 0,
        byMeasureResultsSelectedModel: 0,
    },
});

const publishWaterfallRunGaps = (collector, finalizeOpts) => {
    const {
        baseSiteGJWeighted = {},
        baseEmissions = {},
        packagePercentSavings = {},
        selectedUpgradePackage,
        selectedModels,
        applyModelResultMultiplier,
        denomSiteGJ,
        denomEmissions,
        useDeltaForMeasures,
        allUpgradesEmpty,
        runResultsBatchCount = 0,
        runResultsByMeasureBatchCount = 0,
        checkpointAuditTrail = null,
        byMeasureProbeResult = null,
    } = finalizeOpts;

    const baselineIds = new Set([
        ...Object.keys(baseSiteGJWeighted || {}),
        ...Object.keys(baseEmissions || {}),
    ]);
    const packageIds = new Set(Object.keys(packagePercentSavings || {}));

    const missingPackageRowForBaselineModel = [...baselineIds].filter((uid) => !packageIds.has(uid));
    const packageWithoutBaselineEmissions = [...packageIds].filter((uid) => !(baseEmissions[uid] > 0));
    const packageWithoutBaselineSiteGJ = [...packageIds].filter((uid) => !(baseSiteGJWeighted[uid] > 0));

    const out = {
        generatedAt: new Date().toISOString(),
        howToInspect:
            "Inspect in the console: JSON.stringify(globalThis.__VOLTA_WATERFALL_RUN_GAPS__, null, 2)",
        inputSummary: {
            selectedUpgradePackage,
            selectedModels,
            applyModelResultMultiplier,
            runResultsBatchCount,
            runResultsByMeasureBatchCount,
        },
        denominatorsUsed: {
            denomSiteGJ,
            denomEmissions,
            useDeltaForMeasuresForByMeasurePercents: useDeltaForMeasures,
        },
        waterfallStalledNoMeasureRows: allUpgradesEmpty,
        checkpointAuditTrail,
        ...collector,
        crossCheck: {
            uniqueModelIdsWithBaselineSiteOrEmissions: [...baselineIds].sort(),
            uniqueModelIdsWithSelectedPackageRow: [...packageIds].sort(),
            missingPackageRowForBaselineModel,
            selectedPackageRowButNoBaselineEmissionsRecord: packageWithoutBaselineEmissions.sort(),
            selectedPackageRowButNoBaselineSiteGJRecord: packageWithoutBaselineSiteGJ.sort(),
            notes: [
                "missingPackageRowForBaselineModel: baseline data exists for that model orientation but no whole-package row for the selected package — TOTAL checkpoint omits that unit.",
                "Buckets with fail/skipped/excluded record why a row did not contribute; see samples (capped) for examples.",
                "Whole-package (TOTAL) checkpoint only; combined specialRunType tiers are not used for waterfall scaling.",
                "checkpointAuditTrail: contributing whole-package runs for TOTAL (see Waterfall run coverage → Checkpoint savings audit).",
            ],
        },
    };

    out.diagnosticFocus = buildWaterfallRunDiagnostics(
        out,
        WATERFALL_DIAGNOSTIC_FOCUS_BASE_MODEL_IDS,
        byMeasureProbeResult
    );

    if (typeof globalThis !== "undefined") {
        globalThis.__VOLTA_WATERFALL_RUN_GAPS__ = out;
    }
    return out;
};

/**
 * Waterfall rows: merge `runResultsByMeasure` into `allUpgrades`. Per-measure **display %** is sequence-agnostic:
 * weighted baseline vs by-measure scenario in **GJ** (and tCO₂ for GHG), multi-segment rows splitting the run’s
 * delta equally across segments. Combined `specialRunType` by-measure rows are **excluded** from the merge (they
 * would double-count single-measure rows). Category deltas are **scaled in absolute units** so their sum matches the
 * whole-package weighted delta; chart % = `100 * scaledDelta / community baseline`. When GJ deltas are unavailable,
 * falls back to rescaling mean API % to the same package anchor. Package reference line uses {@link computeGjWeightedPackageSavingsPercents}.
 * Run gaps: `globalThis.__VOLTA_WATERFALL_RUN_GAPS__` (updated every call).
 * @returns {{ waterfall: Array, runGaps: object|null, checkpoints: object|null, axis: { denomSiteGJ, denomEmissions, useDeltaForMeasures } }} `axis` supplies community baseline denominators for optional absolute-unit (GJ / tCO₂) waterfall view. `checkpoints` contains **TOTAL** only. `runGaps.diagnosticFocus.singleMeasureProbe` when {@link WATERFALL_DIAGNOSTIC_PROBE} is set.
 */
export const getWaterfallData = (
    runResults = [],
    runResultsByMeasure = [],
    selectedUpgradePackage,
    selectedModels,
    communityModelCountsMap = {},
    applyModelResultMultiplier = false
) => {
    const allUpgrades = {};
    const dataToReturn = [];
    const baseEmissions = {};
    /** Weighted baseline site energy (GJ) per `uniqueModelId` — same weighting as package rows for duplicates. */
    const baseSiteGJWeighted = {};
    const packageEmissions = {};
    const packagePercentSavings = {};
    const checkpointUpgradeSavings = cloneDeep(CHECKPOINT_SAVINGS_TEMPLATE);
    const runGaps = createWaterfallRunGapsCollector();
    const runResultsBatchCount = Array.isArray(runResults) ? runResults.length : 0;
    const runResultsByMeasureBatchCount = Array.isArray(runResultsByMeasure) ? runResultsByMeasure.length : 0;
    let waterfallRunGapsPublished = null;

    if (!runResultsByMeasure || !runResults) {
        waterfallRunGapsPublished = publishWaterfallRunGaps(runGaps, {
            baseSiteGJWeighted,
            baseEmissions,
            packagePercentSavings,
            selectedUpgradePackage,
            selectedModels,
            applyModelResultMultiplier,
            denomSiteGJ: 0,
            denomEmissions: 0,
            useDeltaForMeasures: false,
            allUpgradesEmpty: true,
            runResultsBatchCount,
            runResultsByMeasureBatchCount,
            checkpointAuditTrail: null,
            byMeasureProbeResult: null,
        });
        return {
            waterfall: dataToReturn,
            runGaps: waterfallRunGapsPublished,
            checkpoints: null,
            axis: { denomSiteGJ: 0, denomEmissions: 0, useDeltaForMeasures: false },
        };
    }

    runResults?.forEach(({ results }) => {
        Object.entries(results).forEach(([resultsId, result]) => {
            const [uniqueModelId, chunkId, combinationId] = resultsId.split("-");
            const [baseModelId, facingDirection] = uniqueModelId.split("_");

            const modelResultMultiplier = Math.max(0, communityModelCountsMap[uniqueModelId] || 1);

            if (!selectedModels.includes(baseModelId)) {
                runGaps.rowCounts.packageResultsNotSelectedModel += 1;
                return;
            }

            runGaps.rowCounts.packageResultsSelectedModel += 1;

            const {
                upgradePackageId = "",
                aecGJ = 0,
                totalEmissionsTCO2 = 0,
                percentSavings = 0,
                percentEmissionSavings = 0,
                result: resultStatus = "",
            } = result;

            if (resultStatus === "FAIL") {
                wfRunGapsAdd(
                    runGaps.packageRuns.fail,
                    wfSnapshotPackageRun(resultsId, result, uniqueModelId, baseModelId, {
                        reason: "resultStatus_FAIL",
                        modelResultMultiplier,
                    })
                );
                return;
            }

            const hasRowPercentSavings = rowHasPercentSavings(percentSavings, percentEmissionSavings);

            if (upgradePackageId === "base") {
                wfRunGapsAdd(
                    runGaps.packageRuns.baselinePackageRow,
                    wfSnapshotPackageRun(resultsId, result, uniqueModelId, baseModelId, {
                        reason: "baseline_row",
                        modelResultMultiplier,
                    })
                );
                const baseSite = siteEnergyGJForPackageWeighting(result);
                if (baseSite > 0) {
                    baseSiteGJWeighted[uniqueModelId] = applyModelResultMultiplier
                        ? baseSite * modelResultMultiplier
                        : baseSite;
                }
                if (aecGJ > 0 || totalEmissionsTCO2 > 0) {
                    // Store baseline GHG Emissions for each model (For calculating missing % savings)
                    baseEmissions[uniqueModelId] = applyModelResultMultiplier
                        ? totalEmissionsTCO2 * modelResultMultiplier
                        : totalEmissionsTCO2;
                }
            }

            // Align with by-measure: allow rows with % savings even when aecGJ is 0 (e.g. PV / generation edge cases)
            if (aecGJ === 0 && !hasRowPercentSavings) {
                wfRunGapsAdd(
                    runGaps.packageRuns.skippedNoAecGjAndNoPercentSavings,
                    wfSnapshotPackageRun(resultsId, result, uniqueModelId, baseModelId, {
                        reason: "no_aecGJ_and_no_percent_savings_fields",
                        modelResultMultiplier,
                    })
                );
                return;
            }

            if (upgradePackageId === selectedUpgradePackage) {
                wfRunGapsAdd(
                    runGaps.packageRuns.includedInTotalCheckpoint,
                    wfSnapshotPackageRun(resultsId, result, uniqueModelId, baseModelId, {
                        reason: "feeds_TOTAL_checkpoint_arrays",
                        modelResultMultiplier,
                    })
                );
                // Store total GHG Emissions from applying all upgrades together for each model (For calculating missing % savings)
                packageEmissions[uniqueModelId] = applyModelResultMultiplier
                    ? totalEmissionsTCO2 * modelResultMultiplier
                    : totalEmissionsTCO2;

                // Store package % savings per model (do not multiply by duplicate count — % stays 0–100 scale).
                // Population weighting for by-measure averages uses divideTotalBy / checkpoint arrays instead.
                packagePercentSavings[uniqueModelId] = {
                    energy: percentSavings,
                    emissions: percentEmissionSavings,
                };

                checkpointUpgradeSavings.TOTAL.upgradePackageId = upgradePackageId;
                checkpointUpgradeSavings.TOTAL.divideTotalBy += applyModelResultMultiplier
                    ? modelResultMultiplier
                    : 1;
                checkpointUpgradeSavings.TOTAL.percentEnergySavingsArray.push(
                    ...new Array(applyModelResultMultiplier ? modelResultMultiplier : 1).fill(percentSavings)
                );
                checkpointUpgradeSavings.TOTAL.percentEmissionSavingsArray.push(
                    ...new Array(applyModelResultMultiplier ? modelResultMultiplier : 1).fill(percentEmissionSavings)
                );
                checkpointUpgradeSavings.TOTAL.auditContributions.push({
                    source: "whole_package_run",
                    resultsId,
                    uniqueModelId,
                    baseModelId,
                    facingDirection,
                    upgradePackageId,
                    duplicateUnitsInMean: applyModelResultMultiplier ? modelResultMultiplier : 1,
                    percentEnergySavings: percentSavings,
                    percentEmissionSavings: percentEmissionSavings,
                    ...auditAbsoluteSnapshotForOrientation(
                        uniqueModelId,
                        result,
                        baseSiteGJWeighted,
                        baseEmissions,
                        applyModelResultMultiplier,
                        modelResultMultiplier
                    ),
                });
            } else if (upgradePackageId !== "base") {
                wfRunGapsAdd(
                    runGaps.packageRuns.wrongOrOtherPackage,
                    wfSnapshotPackageRun(resultsId, result, uniqueModelId, baseModelId, {
                        reason: "not_selected_package_and_not_base",
                        selectedUpgradePackage,
                        modelResultMultiplier,
                    })
                );
            }
        });
    });

    // Calculate missing package % emissions savings if not provided in the results
    Object.entries(packagePercentSavings).forEach(([uniqueModelId, { emissions = 0 }]) => {
        if (!emissions) {
            const baseModelEmissions = baseEmissions[uniqueModelId] || 0;
            const packageModelEmissions = packageEmissions[uniqueModelId] || 0;

            //Base generally assumed to be higher than total
            packagePercentSavings[uniqueModelId].emissions =
                ((baseModelEmissions - packageModelEmissions) / baseModelEmissions) * 100;
        }
    });

    const gjAnchors = computeGjWeightedPackageSavingsPercents(
        runResults,
        selectedModels,
        selectedUpgradePackage,
        communityModelCountsMap,
        applyModelResultMultiplier
    );
    const denomSiteGJ =
        gjAnchors.detail.sumWeightedBaseSiteGJ > 0
            ? gjAnchors.detail.sumWeightedBaseSiteGJ
            : Object.values(baseSiteGJWeighted).reduce((s, v) => s + v, 0);
    const denomEmissions =
        gjAnchors.detail.sumWeightedBaseEmissionsTCO2 > 0
            ? gjAnchors.detail.sumWeightedBaseEmissionsTCO2
            : Object.values(baseEmissions).reduce((s, v) => s + v, 0);

    const deltaGJByType = {};
    const deltaEmByType = {};

    runResultsByMeasure?.forEach(({ results }) => {
        Object.entries(results).forEach(([resultsId, result]) => {
            const [uniqueModelId, chunkId, combinationId] = resultsId.split("-");
            const [baseModelId, facingDirection] = uniqueModelId.split("_");

            const modelResultMultiplier = Math.max(0, communityModelCountsMap[uniqueModelId] || 1);

            if (!selectedModels.includes(baseModelId)) {
                runGaps.rowCounts.byMeasureResultsNotSelectedModel += 1;
                return;
            }

            runGaps.rowCounts.byMeasureResultsSelectedModel += 1;

            {
                const {
                    upgradePackageId = "",
                    listOfUpgrades = "",
                    upgradeIds = "",
                    upgradesPresent = null,
                    specialRunType = "",
                    percentSavings: percentEnergySavingsResult = 0,
                    percentEmissionSavings: percentEmissionSavingsResult = 0,
                    aecGJ = 0,
                    totalEmissionsTCO2 = 0,
                    result: resultStatus = "",
                } = result;

                const hasPercentSavingsFromRun = rowHasPercentSavings(
                    percentEnergySavingsResult,
                    percentEmissionSavingsResult
                );

                if (resultStatus === "FAIL") {
                    wfRunGapsAdd(
                        runGaps.byMeasureRuns.fail,
                        wfSnapshotByMeasureRun(resultsId, result, uniqueModelId, baseModelId, {
                            reason: "resultStatus_FAIL",
                            modelResultMultiplier,
                        })
                    );
                    return;
                }

                if (upgradePackageId === "base") {
                    wfRunGapsAdd(
                        runGaps.byMeasureRuns.excludedBasePackage,
                        wfSnapshotByMeasureRun(resultsId, result, uniqueModelId, baseModelId, {
                            reason: "by_measure_row_is_base_package",
                            modelResultMultiplier,
                        })
                    );
                    return;
                }

                if (upgradePackageId !== selectedUpgradePackage) {
                    wfRunGapsAdd(
                        runGaps.byMeasureRuns.excludedWrongPackage,
                        wfSnapshotByMeasureRun(resultsId, result, uniqueModelId, baseModelId, {
                            reason: "upgradePackageId_mismatch",
                            selectedUpgradePackage,
                            modelResultMultiplier,
                        })
                    );
                    return;
                }

                if (aecGJ === 0 && !hasPercentSavingsFromRun) {
                    wfRunGapsAdd(
                        runGaps.byMeasureRuns.skippedNoAecGjAndNoPercentSavings,
                        wfSnapshotByMeasureRun(resultsId, result, uniqueModelId, baseModelId, {
                            reason: "no_aecGJ_and_no_percent_savings_fields",
                            modelResultMultiplier,
                        })
                    );
                    return;
                }

                let { energy: packagePercentEnergySavings = 0, emissions: packagePercentEmissionSavings = 0 } =
                    packagePercentSavings[uniqueModelId] || {};
                let percentEnergySavings = percentEnergySavingsResult;
                let percentEmissionSavings = percentEmissionSavingsResult;

                // Calculate missing % savings if not provided in the results
                if (!percentEmissionSavings) {
                    const baseModelEmissions = baseEmissions[uniqueModelId] || 0;

                    //Base generally assumed to be higher than total
                    percentEmissionSavings = ((baseModelEmissions - totalEmissionsTCO2) / baseModelEmissions) * 100;
                }

                const wMult = applyModelResultMultiplier ? modelResultMultiplier : 1;
                const useDeltaMethod = denomSiteGJ > 0 && denomEmissions > 0;
                const weightedEnergyValue = wMult * percentEnergySavings;
                const weightedEmissionsValue = wMult * percentEmissionSavings;

                const segments = getResultMeasureSegments(listOfUpgrades, upgradeIds, upgradesPresent);
                if (segments.length === 0) {
                    wfRunGapsAdd(
                        runGaps.byMeasureRuns.noMeasureSegmentsParsed,
                        wfSnapshotByMeasureRun(resultsId, result, uniqueModelId, baseModelId, {
                            reason: "listOfUpgrades_upgradeIds_upgradesPresent_yield_zero_segments",
                            modelResultMultiplier,
                        })
                    );
                    return;
                }

                if (segments.length > 1 && specialRunType !== "") {
                    wfRunGapsAdd(
                        runGaps.byMeasureRuns.includedSpecialCheckpointOnly,
                        wfSnapshotByMeasureRun(resultsId, result, uniqueModelId, baseModelId, {
                            reason: "excluded_special_combined_run_avoids_double_count_with_single_measure_rows",
                            specialRunType,
                            segmentCount: segments.length,
                            modelResultMultiplier,
                        })
                    );
                    return;
                }

                wfRunGapsAdd(
                        runGaps.byMeasureRuns.includedInWaterfallMerge,
                        wfSnapshotByMeasureRun(resultsId, result, uniqueModelId, baseModelId, {
                            reason: "merged_into_measure_bars",
                            segmentCount: segments.length,
                            modelResultMultiplier,
                        })
                    );
                    const n = segments.length;
                    const bSite = baseSiteGJWeighted[uniqueModelId] || 0;
                    const mSite = siteEnergyGJForPackageWeighting(result);
                    const mSiteW = mSite * wMult;
                    const deltaGJRun = useDeltaMethod && bSite > 0 ? bSite - mSiteW : 0;

                    const bEm = baseEmissions[uniqueModelId] || 0;
                    const mEmW = (Number(totalEmissionsTCO2) || 0) * wMult;
                    const deltaEmRun = useDeltaMethod && bEm > 0 ? bEm - mEmW : 0;

                    const splitEnergy = useDeltaMethod ? deltaGJRun / n : weightedEnergyValue / n;
                    const splitEmissions = useDeltaMethod ? deltaEmRun / n : weightedEmissionsValue / n;

                    segments.forEach(({ upgradeType, upgradeId: parsedUpgradeId }, segIdx) => {
                        const pkgPE = segIdx === 0 ? packagePercentEnergySavings : 0;
                        const pkgPM = segIdx === 0 ? packagePercentEmissionSavings : 0;

                        if (useDeltaMethod) {
                            deltaGJByType[upgradeType] = (deltaGJByType[upgradeType] || 0) + splitEnergy;
                            deltaEmByType[upgradeType] = (deltaEmByType[upgradeType] || 0) + splitEmissions;
                        }

                        if (!allUpgrades[upgradeType]) {
                            allUpgrades[upgradeType] = {
                                upgradeId: parsedUpgradeId,
                                upgradeType,
                                upgradePackageId,
                                startEnergy: 0,
                                startEmissions: 0,
                                endEnergy: 0,
                                endEmissions: 0,
                                averagePercentEnergySaving: 0,
                                averagePercentEmissionSaving: 0,
                                packagePercentEnergySavings: pkgPE,
                                packagePercentEmissionSavings: pkgPM,
                                upgradePercentEnergySavings: useDeltaMethod ? 0 : splitEnergy,
                                upgradePercentEmissionSavings: useDeltaMethod ? 0 : splitEmissions,
                                divideTotalBy: useDeltaMethod ? 1 : wMult,
                            };
                        } else {
                            allUpgrades[upgradeType].packagePercentEnergySavings += pkgPE;
                            allUpgrades[upgradeType].packagePercentEmissionSavings += pkgPM;
                            if (!useDeltaMethod) {
                                allUpgrades[upgradeType].upgradePercentEnergySavings += splitEnergy;
                                allUpgrades[upgradeType].upgradePercentEmissionSavings += splitEmissions;
                                allUpgrades[upgradeType].divideTotalBy += wMult;
                            }
                        }
                    });
            }
        });
    });

    const byMeasureProbeResult = collectByMeasureSingleSegmentProbe({
        runResultsByMeasure,
        selectedUpgradePackage,
        selectedModels,
        communityModelCountsMap,
        applyModelResultMultiplier,
        baseSiteGJWeighted,
        baseEmissions,
    });

    if (isEmpty(allUpgrades)) {
        const filledForEmptyChart = fillCheckpointUpgradeSavings(checkpointUpgradeSavings);
        if (gjAnchors.energy != null && Number.isFinite(gjAnchors.energy)) {
            filledForEmptyChart.TOTAL.percentEnergySavings = gjAnchors.energy;
        }
        if (gjAnchors.emissions != null && Number.isFinite(gjAnchors.emissions)) {
            filledForEmptyChart.TOTAL.percentEmissionSavings = gjAnchors.emissions;
        }
        const checkpointAuditTrailEmpty = buildCheckpointAuditTrailForRunGaps(filledForEmptyChart);
        waterfallRunGapsPublished = publishWaterfallRunGaps(runGaps, {
            baseSiteGJWeighted,
            baseEmissions,
            packagePercentSavings,
            selectedUpgradePackage,
            selectedModels,
            applyModelResultMultiplier,
            denomSiteGJ,
            denomEmissions,
            useDeltaForMeasures: denomSiteGJ > 0 && denomEmissions > 0,
            allUpgradesEmpty: true,
            runResultsBatchCount,
            runResultsByMeasureBatchCount,
            checkpointAuditTrail: checkpointAuditTrailEmpty,
            byMeasureProbeResult,
        });
        return {
            waterfall: dataToReturn,
            runGaps: waterfallRunGapsPublished,
            checkpoints: summarizeCheckpointsForWaterfallChart(filledForEmptyChart),
            axis: {
                denomSiteGJ,
                denomEmissions,
                useDeltaForMeasures: denomSiteGJ > 0 && denomEmissions > 0,
            },
        };
    }

    const useDeltaForMeasures = denomSiteGJ > 0 && denomEmissions > 0;
    if (useDeltaForMeasures) {
        Object.keys(allUpgrades).forEach((t) => {
            allUpgrades[t].upgradePercentEnergySavings = (100 * (deltaGJByType[t] || 0)) / denomSiteGJ;
            allUpgrades[t].upgradePercentEmissionSavings = (100 * (deltaEmByType[t] || 0)) / denomEmissions;
            allUpgrades[t].divideTotalBy = 1;
        });
    }

    const filledCheckpointUpgradeSavings = fillCheckpointUpgradeSavings(checkpointUpgradeSavings);
    if (gjAnchors.energy != null && Number.isFinite(gjAnchors.energy)) {
        filledCheckpointUpgradeSavings.TOTAL.percentEnergySavings = gjAnchors.energy;
    }
    if (gjAnchors.emissions != null && Number.isFinite(gjAnchors.emissions)) {
        filledCheckpointUpgradeSavings.TOTAL.percentEmissionSavings = gjAnchors.emissions;
    }

    const chkTotE = filledCheckpointUpgradeSavings.TOTAL?.percentEnergySavings ?? 0;
    const chkTotM = filledCheckpointUpgradeSavings.TOTAL?.percentEmissionSavings ?? 0;
    const targetEnergyPercent =
        gjAnchors.energy != null && Number.isFinite(gjAnchors.energy) ? gjAnchors.energy : chkTotE;
    const targetEmissionsPercent =
        gjAnchors.emissions != null && Number.isFinite(gjAnchors.emissions) ? gjAnchors.emissions : chkTotM;

    const scaledMeasurePercents = computePackageAnchoredScaledMeasurePercents(
        allUpgrades,
        deltaGJByType,
        deltaEmByType,
        useDeltaForMeasures,
        gjAnchors,
        denomSiteGJ,
        denomEmissions,
        targetEnergyPercent,
        targetEmissionsPercent
    );

    const checkpointAuditTrail = buildCheckpointAuditTrailForRunGaps(filledCheckpointUpgradeSavings);

    waterfallRunGapsPublished = publishWaterfallRunGaps(runGaps, {
        baseSiteGJWeighted,
        baseEmissions,
        packagePercentSavings,
        selectedUpgradePackage,
        selectedModels,
        applyModelResultMultiplier,
        denomSiteGJ,
        denomEmissions,
        useDeltaForMeasures,
        allUpgradesEmpty: false,
        runResultsBatchCount,
        runResultsByMeasureBatchCount,
        checkpointAuditTrail,
        byMeasureProbeResult,
    });

    SORTED_UPGRADES_TYPES.forEach((upgradeType, index) => {
        if (allUpgrades[upgradeType]) {
            const {
                packagePercentEnergySavings = 0,
                packagePercentEmissionSavings = 0,
                upgradePercentEnergySavings = 0,
                upgradePercentEmissionSavings = 0,
                divideTotalBy = 1,
            } = allUpgrades[upgradeType];

            const averagePercentEnergySaving =
                scaledMeasurePercents.energy[upgradeType] ?? upgradePercentEnergySavings / divideTotalBy;
            const averagePercentEmissionSaving =
                scaledMeasurePercents.emissions[upgradeType] ?? upgradePercentEmissionSavings / divideTotalBy;

            const {
                startEnergy = 0,
                startEmissions = 0,
                endEnergy = 0,
                endEmissions = 0,
            } = getStartAndEnd(dataToReturn, index, { averagePercentEnergySaving, averagePercentEmissionSaving });

            dataToReturn.push({
                ...allUpgrades[upgradeType],
                startEnergy,
                startEmissions,
                endEnergy,
                endEmissions,
                averagePercentEnergySaving,
                averagePercentEmissionSaving,
            });
        } else {
            const {
                startEnergy = 0,
                startEmissions = 0,
                endEnergy = 0,
                endEmissions = 0,
            } = getStartAndEnd(dataToReturn, index, { averagePercentEnergySaving: 0, averagePercentEmissionSaving: 0 });

            dataToReturn.push({
                upgradeId: null,
                upgradeType: upgradeType,
                startEnergy,
                startEmissions,
                endEnergy,
                endEmissions,
                averagePercentEnergySaving: null,
                averagePercentEmissionSaving: null,
                packagePercentEnergySavings: null,
                packagePercentEmissionSavings: null,
                upgradePercentEnergySavings: null,
                upgradePercentEmissionSavings: null,
                divideTotalBy: null,
            });
        }
    });

    return {
        waterfall: dataToReturn,
        runGaps: waterfallRunGapsPublished,
        checkpoints: summarizeCheckpointsForWaterfallChart(filledCheckpointUpgradeSavings),
        axis: {
            denomSiteGJ,
            denomEmissions,
            useDeltaForMeasures,
        },
    };
};

export const getSavingsTableData = (
    runResultsByMeasure = [],
    selectedUpgradePackage,
    selectedModels,
    communityModelCountsMap = {},
    applyModelResultMultiplier = false
) => {
    const dataToReturn = [];

    if (!runResultsByMeasure) return dataToReturn;

    //Change all base numbers to be the total of the base case
    let totalGJBase = 0;
    let totalElecBase = 0;
    let totalGasBase = 0;
    let totalPropaneBase = 0;
    let totalOilBase = 0;
    let totalWoodBase = 0;
    let totalPVBase = 0;
    let totalNetBase = 0;
    let totalEmissionsBase = 0;

    const allUpgradesTotals = {};

    runResultsByMeasure?.forEach(({ results }) => {
        Object.entries(results).forEach(([resultsId, result]) => {
            const [uniqueModelId, chunkId, combinationId] = resultsId.split("-");
            const [baseModelId, facingDirection] = uniqueModelId.split("_");

            const modelResultMultiplier = Math.max(0, communityModelCountsMap[uniqueModelId] || 1);

            if (selectedModels.includes(baseModelId)) {
                const {
                    upgradePackageId = "",
                    listOfUpgrades = "",
                    upgradeIds = "",
                    upgradesPresent = null,
                    aecGJ = 0,
                    netGJ = 0,
                    elecGJ = 0,
                    ngGJ = 0,
                    propaneGJ = 0,
                    oilGJ = 0,
                    woodGJ = 0,
                    pvGJ = 0,
                    totalEmissionsTCO2 = 0,
                    specialRunType = "",
                } = result;

                if (upgradePackageId !== selectedUpgradePackage && upgradePackageId !== "base") return;

                if (specialRunType !== "") return;

                const weightedGJ = applyModelResultMultiplier ? aecGJ * modelResultMultiplier : aecGJ;
                const weightedElec = applyModelResultMultiplier ? elecGJ * modelResultMultiplier : elecGJ;
                const weightedGas = applyModelResultMultiplier ? ngGJ * modelResultMultiplier : ngGJ;
                const weightedPropane = applyModelResultMultiplier ? propaneGJ * modelResultMultiplier : propaneGJ;
                const weightedOil = applyModelResultMultiplier ? oilGJ * modelResultMultiplier : oilGJ;
                const weightedWood = applyModelResultMultiplier ? woodGJ * modelResultMultiplier : woodGJ;
                const weightedPV = applyModelResultMultiplier ? pvGJ * modelResultMultiplier : pvGJ;
                const weightedNet = applyModelResultMultiplier ? netGJ * modelResultMultiplier : netGJ;
                const weightedEmissions = applyModelResultMultiplier
                    ? totalEmissionsTCO2 * modelResultMultiplier
                    : totalEmissionsTCO2;

                if (upgradePackageId === "base") {
                    totalGJBase += weightedGJ;
                    totalElecBase += weightedElec;
                    totalGasBase += weightedGas;
                    totalPropaneBase += weightedPropane;
                    totalOilBase += weightedOil;
                    totalWoodBase += weightedWood;
                    totalPVBase += weightedPV;
                    totalNetBase += weightedNet;
                    totalEmissionsBase += weightedEmissions;
                } else {
                    const segments = getResultMeasureSegments(listOfUpgrades, upgradeIds, upgradesPresent);
                    if (segments.length === 0) {
                        return;
                    }

                    const n = segments.length;
                    const splitGJ = weightedGJ / n;
                    const splitElec = weightedElec / n;
                    const splitGas = weightedGas / n;
                    const splitPropane = weightedPropane / n;
                    const splitOil = weightedOil / n;
                    const splitWood = weightedWood / n;
                    const splitPV = weightedPV / n;
                    const splitNet = weightedNet / n;
                    const splitEmissions = weightedEmissions / n;

                    segments.forEach(({ upgradeType, upgradeId: parsedUpgradeId }) => {
                        if (!allUpgradesTotals[upgradeType]) {
                            allUpgradesTotals[upgradeType] = {
                                upgradeId: parsedUpgradeId,
                                upgradeType,
                                upgradePackageId,
                                totalGJ: splitGJ,
                                totalElec: splitElec,
                                totalGas: splitGas,
                                totalPropane: splitPropane,
                                totalOil: splitOil,
                                totalWood: splitWood,
                                totalPV: splitPV,
                                totalNet: splitNet,
                                totalEmissions: splitEmissions,
                            };
                        } else {
                            allUpgradesTotals[upgradeType].totalGJ += splitGJ;
                            allUpgradesTotals[upgradeType].totalElec += splitElec;
                            allUpgradesTotals[upgradeType].totalGas += splitGas;
                            allUpgradesTotals[upgradeType].totalPropane += splitPropane;
                            allUpgradesTotals[upgradeType].totalOil += splitOil;
                            allUpgradesTotals[upgradeType].totalWood += splitWood;
                            allUpgradesTotals[upgradeType].totalPV += splitPV;
                            allUpgradesTotals[upgradeType].totalNet += splitNet;
                            allUpgradesTotals[upgradeType].totalEmissions += splitEmissions;
                        }
                    });
                }
            }
        });
    });

    if (isEmpty(allUpgradesTotals)) return dataToReturn;

    SORTED_UPGRADES_TYPES.forEach((upgradeType, index) => {
        if (allUpgradesTotals[upgradeType]) {
            const {
                totalGJ = 0,
                totalElec = 0,
                totalGas = 0,
                totalPropane = 0,
                totalOil = 0,
                totalWood = 0,
                totalPV = 0,
                totalNet = 0,
                totalEmissions = 0,
            } = allUpgradesTotals[upgradeType]; //All totals in GJ except for emissions

            dataToReturn.push({
                upgradeId: allUpgradesTotals[upgradeType].upgradeId,
                upgradeType: allUpgradesTotals[upgradeType].upgradeType,
                aecSavingsGJ: totalGJBase - totalGJ,
                netSavingsGJ: totalNetBase - totalNet,
                elecSavingsKWH: (totalElecBase - totalElec) / energyConversion.elecGJperkWh,
                gasSavingsM3: (totalGasBase - totalGas) / energyConversion.ngGJperm3,
                propaneSavingsL: (totalPropaneBase - totalPropane) / energyConversion.propaneGJperL,
                oilSavingsL: (totalOilBase - totalOil) / energyConversion.oilGJperL,
                woodSavingsCord: (totalWoodBase - totalWood) / energyConversion.woodGJperCord,
                pvSavingsGJ: totalPVBase - totalPV,
                emissionsSavingsTCO2: totalEmissionsBase - totalEmissions,
            });
        } else {
            dataToReturn.push({
                upgradeId: null,
                upgradeType: upgradeType,
                aecSavingsGJ: 0,
                netSavingsGJ: 0,
                elecSavingsKWH: 0,
                gasSavingsM3: 0,
                propaneSavingsL: 0,
                oilSavingsL: 0,
                woodSavingsCord: 0,
                pvSavingsGJ: 0,
                emissionsSavingsTCO2: 0,
            });
        }
    });

    return dataToReturn;
};

// Get the start and end values for the waterfall bars
export const getStartAndEnd = (waterfallData, index, item = {}) => {
    // `null` N/A placeholders from getWaterfallData must not bypass defaults (destructuring would keep null).
    const averagePercentEnergySaving = item.averagePercentEnergySaving ?? 0;
    const averagePercentEmissionSaving = item.averagePercentEmissionSaving ?? 0;
    const prev = index > 0 ? waterfallData[index - 1] : undefined;
    const prevEndEnergy = prev?.endEnergy ?? 100;
    const prevEndEmissions = prev?.endEmissions ?? 100;

    return {
        startEnergy: index === 0 ? 100 : prevEndEnergy,
        startEmissions: index === 0 ? 100 : prevEndEmissions,
        endEnergy: index === 0 ? 100 - averagePercentEnergySaving : prevEndEnergy - averagePercentEnergySaving,
        endEmissions:
            index === 0 ? 100 - averagePercentEmissionSaving : prevEndEmissions - averagePercentEmissionSaving,
    };
};

// Check if the item should be hidden from the graph or table
export const isHidden = (item, type = "waterfall", isEnergyPercent = false) => {
    if (type === "waterfall") {
        const v = isEnergyPercent ? item.averagePercentEnergySaving : item.averagePercentEmissionSaving;
        // Placeholder rows use null; real 0% savings must remain visible (e.g. population-weighted averages).
        return v == null;
    }
    if (type === "table") {
        if (item.upgradeId != null) {
            return false;
        }
        // N/A placeholder row: hide when every numeric field is still zero / empty.
        return Object.entries(item).every(([key, value]) =>
            ["upgradeId", "upgradeType"].includes(key) || value == null || value === "" || value === 0
        );
    }
};
